// src/components/ForumPage.jsx
import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { API } from "../utils/constants";
import { FORUM_ORIGIN, isForumDomain } from "../utils/siteUrls";
import {
  ChevronRight, ChevronLeft, MessageCircle, Eye, Clock,
  Pin, Lock, Unlock, Plus, Send, User, AlertCircle, Trash2, Archive, RotateCcw,
  Megaphone, BookOpen, Wrench, ShieldAlert,
  FileText, Swords, Dumbbell, Map, Pencil, Check, X, Code2, Link2,
} from "lucide-react";

const CSS = `
  .ff { font-family:'Inter',sans-serif; color:#e8e0f0; }

  @keyframes ffade { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:none} }
  .fa  { animation:ffade .35s ease both; }
  .fa1 { animation-delay:.04s; }
  .fa2 { animation-delay:.08s; }
  .fa3 { animation-delay:.12s; }
  .fa4 { animation-delay:.16s; }
  .fa5 { animation-delay:.20s; }
  .fa6 { animation-delay:.24s; }

  @keyframes fsk { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
  .fsk { background:linear-gradient(90deg,rgba(255,255,255,.03) 25%,rgba(255,255,255,.07) 50%,rgba(255,255,255,.03) 75%);
         background-size:200% 100%;animation:fsk 1.5s infinite;border-radius:4px; }

  .fcat-row {
    display:flex;align-items:stretch;
    border-bottom:1px solid rgba(42,32,64,.6);
    transition:background .15s;cursor:pointer;
    background:none;border-left:none;border-right:none;border-top:none;
    width:100%;text-align:left;padding:0;
  }
  .fcat-row:hover { background:rgba(123,94,167,.04); }
  .fcat-row:hover .fcat-arrow { opacity:1;transform:translateX(0); }
  .fcat-arrow { opacity:0;transform:translateX(-5px);transition:opacity .18s,transform .18s;flex-shrink:0;margin-right:20px;color:#4a3870; }

  .fth-row {
    display:flex;align-items:center;gap:14px;
    padding:13px 24px;
    border-bottom:1px solid rgba(42,32,64,.5);
    cursor:pointer;transition:background .13s;
  }
  .fth-row:last-child{border-bottom:none}
  .fth-row:hover{background:rgba(123,94,167,.04)}

  .fsub-row {
    display:flex;align-items:center;gap:12px;
    padding:12px 24px 12px 28px;
    border-bottom:1px solid rgba(42,32,64,.4);
    cursor:pointer;transition:background .13s;
    background:none;border-left:none;border-right:none;border-top:none;width:100%;text-align:left;
  }
  .fsub-row:last-child{border-bottom:none}
  .fsub-row:hover{background:rgba(123,94,167,.04)}

  .fmini-row {
    display:flex;align-items:center;gap:10px;
    padding:7px 24px 7px 48px;
    cursor:pointer;transition:background .12s;
    background:none;border:none;width:100%;text-align:left;
    border-bottom:1px solid rgba(42,32,64,.3);
  }
  .fmini-row:last-child{border-bottom:none}
  .fmini-row:hover{background:rgba(123,94,167,.04)}

  .fbtn {
    display:inline-flex;align-items:center;gap:6px;
    font-family:'Cinzel',serif;font-size:10px;letter-spacing:2px;text-transform:uppercase;
    padding:8px 18px;border-radius:6px;cursor:pointer;transition:all .2s;border:none;
  }
  .fbtn:disabled{opacity:.4;cursor:not-allowed}
  .fbtn-p{background:linear-gradient(135deg,#7b5ea7,#5a3d8a);color:#fff;box-shadow:0 0 14px rgba(123,94,167,.28)}
  .fbtn-p:hover:not(:disabled){box-shadow:0 0 22px rgba(123,94,167,.45);transform:translateY(-1px)}
  .fbtn-g{background:transparent;border:1px solid #2a2040;color:#a89cc8}
  .fbtn-g:hover:not(:disabled){border-color:#4a3870;color:#e8e0f0}

  .fcrumb{background:none;border:none;cursor:pointer;padding:0;display:flex;align-items:center;gap:4px;
    color:#6b6080;font-size:11px;font-family:'Inter',sans-serif;font-weight:500;
    transition:color .15s;letter-spacing:.3px}
  .fcrumb:hover{color:#a89cc8}

  .finput{width:100%;padding:10px 14px;background:rgba(255,255,255,.04);
    border:1px solid #2a2040;border-radius:8px;outline:none;
    color:#e8e0f0;font-family:'Inter',sans-serif;font-size:14px;
    transition:border-color .2s;box-sizing:border-box}
  .finput:focus{border-color:#7b5ea7;box-shadow:0 0 0 3px rgba(123,94,167,.1)}
  .ftextarea{width:100%;min-height:120px;padding:12px 14px;
    background:rgba(255,255,255,.04);border:1px solid #2a2040;
    border-radius:8px;outline:none;color:#e8e0f0;
    font-family:'Inter',sans-serif;font-size:14px;line-height:1.7;
    resize:vertical;transition:border-color .2s;box-sizing:border-box}
  .ftextarea:focus{border-color:#7b5ea7;box-shadow:0 0 0 3px rgba(123,94,167,.1)}

  .fdiv{height:1px;background:linear-gradient(90deg,transparent,rgba(74,56,112,.6) 30%,rgba(74,56,112,.6) 70%,transparent);margin:32px 0}

  .fauth{width:120px;flex-shrink:0;padding:20px 14px;display:flex;flex-direction:column;
    align-items:center;gap:6px;text-align:center;
    border-right:1px solid rgba(42,32,64,.5)}

  /* ── Vote widget ── */
  .fvote{display:flex;flex-direction:column;align-items:center;gap:1px;flex-shrink:0;
    padding:14px 8px;user-select:none}
  .fvote-btn{background:none;border:none;cursor:pointer;padding:3px 6px;border-radius:3px;
    display:flex;align-items:center;justify-content:center;
    transition:color .12s,background .12s;line-height:1}
  .fvote-btn:hover:not(:disabled){background:rgba(255,255,255,.06)}
  .fvote-btn:disabled{cursor:default;opacity:.35}
  .fvote-count{font-family:'Cinzel',serif;font-size:10px;font-weight:700;line-height:1;
    min-width:18px;text-align:center;transition:color .2s}

  /* ── Reddit-style create bar ── */
  .fcreate-bar{display:flex;align-items:center;gap:10px;padding:9px 16px;
    background:rgba(255,255,255,.02);border-bottom:1px solid rgba(42,32,64,.5)}
  .fcreate-fake-input{flex:1;padding:7px 14px;background:rgba(255,255,255,.04);
    border:1px solid rgba(42,32,64,.7);border-radius:4px;font-family:'Inter',sans-serif;
    font-size:13px;color:#4a3870;cursor:pointer;transition:border-color .15s,color .15s;outline:none}
  .fcreate-fake-input:hover{border-color:#4a3870;color:#a89cc8}

  @media(max-width:640px){
    .fauth{display:none}
    .fhide{display:none !important}
    .fauth-mobile{display:flex !important}
  }
  @media(min-width:641px){
    .fauth-mobile{display:none !important}
  }
  .fauth-mobile{
    display:none;align-items:center;gap:8px;
    padding:8px 14px 0;flex-shrink:0;
  }

  /* ── Rich editor ── */
  .frich-wrap{border:1px solid #2a2040;border-radius:8px;overflow:hidden;
    transition:border-color .2s;background:rgba(255,255,255,.04)}
  .frich-wrap:focus-within{border-color:#7b5ea7;box-shadow:0 0 0 3px rgba(123,94,167,.1)}

  .frich-toolbar{display:flex;flex-wrap:wrap;gap:2px;padding:6px 8px;
    background:rgba(255,255,255,.03);border-bottom:1px solid rgba(42,32,64,.7)}
  .frich-sep{width:1px;background:rgba(74,56,112,.5);margin:2px 4px;align-self:stretch}
  .ftb{background:none;border:none;cursor:pointer;border-radius:4px;
    padding:4px 7px;font-size:12px;color:#6b6080;transition:color .12s,background .12s;
    display:flex;align-items:center;justify-content:center;min-width:26px;min-height:26px;line-height:1}
  .ftb:hover{background:rgba(123,94,167,.15);color:#a89cc8}
  .ftb.active{background:rgba(123,94,167,.25);color:#c9a84c}

  .frich-editor{min-height:160px;max-height:480px;overflow-y:auto;
    padding:12px 14px;outline:none;font-family:'Inter',sans-serif;
    font-size:14px;line-height:1.78;color:#c8c0e0;word-break:break-word}
  .frich-editor:empty::before{content:attr(data-placeholder);color:#4a3870;pointer-events:none}
  .frich-editor h1{font-family:'Cinzel Decorative',serif;font-size:24px;font-weight:700;
    color:#f0e8ff;margin:18px 0 8px;letter-spacing:.4px;line-height:1.3}
  .frich-editor h2{font-family:'Cinzel',serif;font-size:19px;font-weight:700;
    color:#e8e0f0;margin:14px 0 6px;letter-spacing:.3px}
  .frich-editor h3{font-family:'Cinzel',serif;font-size:16px;font-weight:700;
    color:#d0c8e8;margin:12px 0 5px;letter-spacing:.2px}
  .frich-editor strong{color:#e8e0f0;font-weight:700}
  .frich-editor em{color:#c0b8d8;font-style:italic}
  .frich-editor u{text-decoration:underline;text-decoration-color:rgba(155,127,212,.5)}
  .frich-editor a{color:#9b7fd4;text-decoration:underline;word-break:break-all}
  .frich-editor a:hover{color:#c9a84c}
  .frich-editor blockquote{border-left:3px solid #4a3870;margin:10px 0;
    padding:8px 14px;background:rgba(74,56,112,.12);border-radius:0 6px 6px 0;
    color:#a89cc8;font-style:italic}
  .frich-editor ul,.frich-editor ol{margin:4px 0 4px 22px;padding:0;line-height:1.4}
  .frich-editor li{margin:0!important;padding:0!important;line-height:1.4}
  .frich-editor li+li{margin-top:0!important}
  .frich-editor li>div,.frich-editor li>p{margin:0!important;padding:0!important;line-height:inherit}
  .frich-editor code{background:rgba(74,56,112,.25);border:1px solid rgba(74,56,112,.4);
    border-radius:3px;padding:1px 5px;font-family:'Courier New',monospace;font-size:13px;color:#c9a84c}
  .frich-editor pre{background:rgba(10,8,16,.6);border:1px solid rgba(74,56,112,.5);
    border-radius:6px;padding:12px 14px;margin:10px 0;overflow-x:auto}
  .frich-editor pre code{background:none;border:none;padding:0;color:#a0e0a0;font-size:13px}
  .frich-editor img{max-width:min(760px,100%);height:auto;border-radius:8px;margin:12px auto;
    border:1px solid rgba(74,56,112,.3);display:block}
  .frich-editor hr{border:none;border-top:1px solid rgba(74,56,112,.5);margin:16px 0}
  .frich-editor p{margin:4px 0}

  .frich-editor font[size="1"]{font-size:11px}
  .frich-editor font[size="2"]{font-size:13px}
  .frich-editor font[size="3"]{font-size:15px}
  .frich-editor font[size="4"]{font-size:18px}
  .frich-editor font[size="5"]{font-size:22px}

  /* ── Rich rendered post body ── */
  .fpost-html h1{font-family:'Cinzel Decorative',serif;font-size:24px;font-weight:700;
    color:#f0e8ff;margin:18px 0 8px;letter-spacing:.4px;line-height:1.3}
  .fpost-html h2{font-family:'Cinzel',serif;font-size:19px;font-weight:700;
    color:#e8e0f0;margin:16px 0 7px;letter-spacing:.3px}
  .fpost-html h3{font-family:'Cinzel',serif;font-size:16px;font-weight:700;
    color:#d0c8e8;margin:13px 0 5px}
  .fpost-html strong{color:#e8e0f0;font-weight:700}
  .fpost-html em{color:#c0b8d8;font-style:italic}
  .fpost-html u{text-decoration:underline;text-decoration-color:rgba(155,127,212,.5)}
  .fpost-html a{color:#9b7fd4;text-decoration:underline;word-break:break-all}
  .fpost-html a:hover{color:#c9a84c}
  .fpost-html blockquote{border-left:3px solid #4a3870;margin:10px 0;
    padding:8px 14px;background:rgba(74,56,112,.12);border-radius:0 6px 6px 0;
    color:#a89cc8;font-style:italic}
  .fpost-html ul,.fpost-html ol{margin:4px 0 4px 22px;padding:0;line-height:1.4}
  .fpost-html li{margin:0!important;padding:0!important;line-height:1.4}
  .fpost-html li+li{margin-top:0!important}
  .fpost-html li>div,.fpost-html li>p{margin:0!important;padding:0!important;line-height:inherit}
  .fpost-html code{background:rgba(74,56,112,.25);border:1px solid rgba(74,56,112,.4);
    border-radius:3px;padding:1px 5px;font-family:'Courier New',monospace;font-size:13px;color:#c9a84c}
  .fpost-html pre{background:rgba(10,8,16,.6);border:1px solid rgba(74,56,112,.5);
    border-radius:6px;padding:12px 14px;margin:10px 0;overflow-x:auto}
  .fpost-html pre code{background:none;border:none;padding:0;color:#a0e0a0;font-size:13px}
  .fpost-html img{max-width:min(760px,100%);height:auto;border-radius:8px;margin:12px auto;
    border:1px solid rgba(74,56,112,.3);display:block}
  .fpost-html img.fmd-image-small{width:min(480px,100%);max-width:100%}
  .fpost-html img.fmd-image-medium{width:min(760px,100%);max-width:100%}
  .fpost-html img.fmd-image-large{width:min(1000px,100%);max-width:100%}
  .fpost-html img.fmd-image-full{width:100%;max-width:100%}
  .fpost-html img.fmd-image-left{margin-left:0;margin-right:auto}
  .fpost-html img.fmd-image-right{margin-left:auto;margin-right:0}
  .fpost-html hr{border:none;border-top:1px solid rgba(74,56,112,.5);margin:16px 0}
  .fpost-html p{margin:4px 0}
  .fpost-html font[size="1"]{font-size:11px}
  .fpost-html font[size="2"]{font-size:13px}
  .fpost-html font[size="3"]{font-size:15px}
  .fpost-html font[size="4"]{font-size:18px}
  .fpost-html font[size="5"]{font-size:22px}

  .frich-link-row{display:flex;gap:6px;padding:8px;
    background:rgba(10,8,16,.5);border-top:1px solid rgba(42,32,64,.7)}
  .frich-link-row input{flex:1;padding:6px 10px;background:rgba(255,255,255,.04);
    border:1px solid #2a2040;border-radius:5px;outline:none;color:#e8e0f0;
    font-family:'Inter',sans-serif;font-size:13px}
  .frich-link-row input:focus{border-color:#7b5ea7}
  .frich-img-row{display:flex;gap:6px;padding:8px;
    background:rgba(10,8,16,.5);border-top:1px solid rgba(42,32,64,.7)}
  .frich-img-row input{flex:1;padding:6px 10px;background:rgba(255,255,255,.04);
    border:1px solid #2a2040;border-radius:5px;outline:none;color:#e8e0f0;
    font-family:'Inter',sans-serif;font-size:13px}
  .frich-img-row input:focus{border-color:#7b5ea7}


  /* ── Forum guide Markdown components ── */
  .fpost-html .fmd-icon{
    display:inline-block;width:1em;height:1em;vertical-align:-.14em;
    margin-right:.34em;flex:0 0 auto;background:currentColor;
    -webkit-mask-repeat:no-repeat;mask-repeat:no-repeat;
    -webkit-mask-position:center;mask-position:center;
    -webkit-mask-size:contain;mask-size:contain;
  }
  .fpost-html h1 .fmd-icon,.fpost-html h2 .fmd-icon,.fpost-html h3 .fmd-icon{
    color:#9b7fd4;margin-right:.42em;
  }
  .fpost-html blockquote .fmd-icon{color:#c9a84c}

  .fpost-html .fmd-download{
    display:inline-flex;align-items:center;gap:8px;
    margin:8px 0 10px;padding:10px 16px;
    border:1px solid rgba(155,127,212,.55);border-radius:7px;
    background:linear-gradient(135deg,rgba(123,94,167,.95),rgba(90,61,138,.95));
    box-shadow:0 5px 18px rgba(40,24,68,.28);
    color:#fff !important;text-decoration:none !important;
    font-family:'Cinzel',serif;font-size:11px;font-weight:700;
    letter-spacing:.8px;transition:transform .16s,box-shadow .16s,border-color .16s;
  }
  .fpost-html .fmd-download:hover{
    transform:translateY(-1px);
    border-color:rgba(201,168,76,.65);
    box-shadow:0 8px 22px rgba(70,43,112,.38);
    color:#fff !important;
  }
  .fpost-html .fmd-download .fmd-icon{margin-right:0;color:#fff;width:15px;height:15px}

  /* Lucide-style vector masks used by [icon:name] shortcodes. */
  .fmd-icon-monitor{
    -webkit-mask-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='2' y='3' width='20' height='14' rx='2'/%3E%3Cpath d='M8 21h8M12 17v4'/%3E%3C/svg%3E");
    mask-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='2' y='3' width='20' height='14' rx='2'/%3E%3Cpath d='M8 21h8M12 17v4'/%3E%3C/svg%3E");
  }
  .fmd-icon-clipboard{
    -webkit-mask-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='5' y='4' width='14' height='17' rx='2'/%3E%3Cpath d='M9 4V2h6v2M9 9h6M9 13h6M9 17h4'/%3E%3C/svg%3E");
    mask-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='5' y='4' width='14' height='17' rx='2'/%3E%3Cpath d='M9 4V2h6v2M9 9h6M9 13h6M9 17h4'/%3E%3C/svg%3E");
  }
  .fmd-icon-alert{
    -webkit-mask-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M10.3 2.9 1.8 17a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 2.9a2 2 0 0 0-3.4 0Z'/%3E%3Cpath d='M12 9v4M12 17h.01'/%3E%3C/svg%3E");
    mask-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M10.3 2.9 1.8 17a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 2.9a2 2 0 0 0-3.4 0Z'/%3E%3Cpath d='M12 9v4M12 17h.01'/%3E%3C/svg%3E");
  }
  .fmd-icon-download{
    -webkit-mask-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3'/%3E%3C/svg%3E");
    mask-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3'/%3E%3C/svg%3E");
  }
  .fmd-icon-package{
    -webkit-mask-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m7.5 4.3 9 5.2M3.3 7l8.7 5 8.7-5M12 22V12'/%3E%3Cpath d='M21 16V8a2 2 0 0 0-1-1.7l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.7l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z'/%3E%3C/svg%3E");
    mask-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m7.5 4.3 9 5.2M3.3 7l8.7 5 8.7-5M12 22V12'/%3E%3Cpath d='M21 16V8a2 2 0 0 0-1-1.7l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.7l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z'/%3E%3C/svg%3E");
  }
  .fmd-icon-folder{
    -webkit-mask-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M3 5a2 2 0 0 1 2-2h4l2 3h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z'/%3E%3C/svg%3E");
    mask-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M3 5a2 2 0 0 1 2-2h4l2 3h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z'/%3E%3C/svg%3E");
  }
  .fmd-icon-file{
    -webkit-mask-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z'/%3E%3Cpath d='M14 2v6h6'/%3E%3C/svg%3E");
    mask-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z'/%3E%3Cpath d='M14 2v6h6'/%3E%3C/svg%3E");
  }
  .fmd-icon-play{
    -webkit-mask-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Ccircle cx='12' cy='12' r='10'/%3E%3Cpath d='m10 8 6 4-6 4Z'/%3E%3C/svg%3E");
    mask-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Ccircle cx='12' cy='12' r='10'/%3E%3Cpath d='m10 8 6 4-6 4Z'/%3E%3C/svg%3E");
  }
  .fmd-icon-settings{
    -webkit-mask-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M12 15.5A3.5 3.5 0 1 0 12 8a3.5 3.5 0 0 0 0 7.5Z'/%3E%3Cpath d='M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06-2.83 2.83-.06-.06A1.7 1.7 0 0 0 15 19.4a1.7 1.7 0 0 0-1 .6 1.7 1.7 0 0 0-.4 1.1V21H9.6v-.1A1.7 1.7 0 0 0 8.5 19.4a1.7 1.7 0 0 0-1.88.34l-.06.06-2.83-2.83.06-.06A1.7 1.7 0 0 0 4.1 15a1.7 1.7 0 0 0-.6-1 1.7 1.7 0 0 0-1.1-.4H2.3V9.6h.1A1.7 1.7 0 0 0 4.1 8.5a1.7 1.7 0 0 0-.34-1.88L3.7 6.56l2.83-2.83.06.06A1.7 1.7 0 0 0 8.5 4.1a1.7 1.7 0 0 0 1-.6 1.7 1.7 0 0 0 .4-1.1V2.3h4.1v.1A1.7 1.7 0 0 0 15 4.1a1.7 1.7 0 0 0 1.88-.34l.06-.06 2.83 2.83-.06.06A1.7 1.7 0 0 0 19.4 8.5a1.7 1.7 0 0 0 .6 1 1.7 1.7 0 0 0 1.1.4h.1V14h-.1a1.7 1.7 0 0 0-1.7 1Z'/%3E%3C/svg%3E");
    mask-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M12 15.5A3.5 3.5 0 1 0 12 8a3.5 3.5 0 0 0 0 7.5Z'/%3E%3Cpath d='M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06-2.83 2.83-.06-.06A1.7 1.7 0 0 0 15 19.4a1.7 1.7 0 0 0-1 .6 1.7 1.7 0 0 0-.4 1.1V21H9.6v-.1A1.7 1.7 0 0 0 8.5 19.4a1.7 1.7 0 0 0-1.88.34l-.06.06-2.83-2.83.06-.06A1.7 1.7 0 0 0 4.1 15a1.7 1.7 0 0 0-.6-1 1.7 1.7 0 0 0-1.1-.4H2.3V9.6h.1A1.7 1.7 0 0 0 4.1 8.5a1.7 1.7 0 0 0-.34-1.88L3.7 6.56l2.83-2.83.06.06A1.7 1.7 0 0 0 8.5 4.1a1.7 1.7 0 0 0 1-.6 1.7 1.7 0 0 0 .4-1.1V2.3h4.1v.1A1.7 1.7 0 0 0 15 4.1a1.7 1.7 0 0 0 1.88-.34l.06-.06 2.83 2.83-.06.06A1.7 1.7 0 0 0 19.4 8.5a1.7 1.7 0 0 0 .6 1 1.7 1.7 0 0 0 1.1.4h.1V14h-.1a1.7 1.7 0 0 0-1.7 1Z'/%3E%3C/svg%3E");
  }
  .fmd-icon-circle-help{
    -webkit-mask-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Ccircle cx='12' cy='12' r='10'/%3E%3Cpath d='M9.1 9a3 3 0 1 1 5.8 1c0 2-3 2-3 4M12 18h.01'/%3E%3C/svg%3E");
    mask-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Ccircle cx='12' cy='12' r='10'/%3E%3Cpath d='M9.1 9a3 3 0 1 1 5.8 1c0 2-3 2-3 4M12 18h.01'/%3E%3C/svg%3E");
  }
  .fmd-icon-refresh-cw{
    -webkit-mask-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M21 12a9 9 0 0 1-15.5 6.2L3 16M3 12A9 9 0 0 1 18.5 5.8L21 8'/%3E%3Cpath d='M21 3v5h-5M3 21v-5h5'/%3E%3C/svg%3E");
    mask-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M21 12a9 9 0 0 1-15.5 6.2L3 16M3 12A9 9 0 0 1 18.5 5.8L21 8'/%3E%3Cpath d='M21 3v5h-5M3 21v-5h5'/%3E%3C/svg%3E");
  }
  .fmd-icon-trash{
    -webkit-mask-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M3 6h18M8 6V4h8v2M19 6l-1 15H6L5 6M10 11v6M14 11v6'/%3E%3C/svg%3E");
    mask-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M3 6h18M8 6V4h8v2M19 6l-1 15H6L5 6M10 11v6M14 11v6'/%3E%3C/svg%3E");
  }
  .fmd-icon-life-buoy{
    -webkit-mask-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Ccircle cx='12' cy='12' r='10'/%3E%3Ccircle cx='12' cy='12' r='4'/%3E%3Cpath d='m4.9 4.9 4.3 4.3M14.8 14.8l4.3 4.3M19.1 4.9l-4.3 4.3M9.2 14.8l-4.3 4.3'/%3E%3C/svg%3E");
    mask-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Ccircle cx='12' cy='12' r='10'/%3E%3Ccircle cx='12' cy='12' r='4'/%3E%3Cpath d='m4.9 4.9 4.3 4.3M14.8 14.8l4.3 4.3M19.1 4.9l-4.3 4.3M9.2 14.8l-4.3 4.3'/%3E%3C/svg%3E");
  }

  /* ── Desktop enlargement ── */
  @media(min-width:900px){
    .fcat-row .fcat-icon { width:44px !important; height:44px !important; margin:20px 20px !important; }
    .fcat-row .fcat-label { font-size:16px !important; }
    .fcat-row .fcat-desc  { font-size:13.5px !important; }
    .fsub-row { padding:14px 32px 14px 36px !important; }
    .fsub-row .fsub-label { font-size:13.5px !important; }
    .fsub-row .fsub-desc  { font-size:12.5px !important; }
    .fmini-row { padding:8px 32px 8px 60px !important; }
    .fth-row { padding:16px 32px !important; gap:16px !important; }
    .fcreate-bar { padding:12px 24px !important; }
    .fpost-html, .frich-editor { font-size:15px !important; }
  }
  @media(min-width:1200px){
    .fsub-row { padding:15px 40px 15px 44px !important; }
    .fmini-row { padding:9px 40px 9px 70px !important; }
    .fth-row { padding:17px 40px !important; }
    .fcreate-bar { padding:13px 32px !important; }
    .fauth { width:140px !important; padding:24px 18px !important; }
  }
`;

// ── HTML content helpers ──────────────────────────────────────────────────────
const HTML_PREFIX = "<!--HTML-->";

/** Strip only dangerous tags/attrs from HTML. No external lib needed. */
function sanitizeHtml(html) {
  const ALLOWED_TAGS = new Set([
    "p","br","strong","b","em","i","u","s","h1","h2","h3","font","ul","ol","li",
    "blockquote","code","pre","hr","a","img","span","div",
  ]);
  // Tags whose "content" is raw text data (CSS/JS/markup source), not
  // document content. These must be deleted entirely — unwrapping them
  // like a normal disallowed tag would dump their raw text into the post.
  const STRIP_ENTIRELY = new Set([
    "script","style","head","title","meta","link","noscript","template",
    "svg","iframe","object","embed","form","html","body",
  ]);
  const ALLOWED_ATTRS = {
    a:   ["href","target","rel"],
    img:  ["src","alt","width","height"],
    font: ["size"],
    "*": ["class"],
  };
  const doc = new DOMParser().parseFromString(html, "text/html");
  function clean(node) {
    if (node.nodeType === 3) return; // text node — safe
    if (node.nodeType !== 1) { node.parentNode?.removeChild(node); return; }
    const tag = node.tagName.toLowerCase();
    if (STRIP_ENTIRELY.has(tag)) {
      node.parentNode?.removeChild(node);
      return;
    }
    if (!ALLOWED_TAGS.has(tag)) {
      // Replace disallowed element with its children
      node.replaceWith(...Array.from(node.childNodes));
      return;
    }
    // Strip disallowed attributes
    const allowed = [...(ALLOWED_ATTRS[tag] || []), ...(ALLOWED_ATTRS["*"] || [])];
    for (const attr of [...node.attributes]) {
      if (!allowed.includes(attr.name)) node.removeAttribute(attr.name);
    }
    // Force safe link targets and strip javascript: hrefs
    if (tag === "a") {
      const href = node.getAttribute("href") || "";
      if (/^javascript:/i.test(href)) node.removeAttribute("href");
      node.setAttribute("target", "_blank");
      node.setAttribute("rel", "noopener noreferrer");
    }
    // Strip data: image sources (allow only http/https)
    if (tag === "img") {
      const src = node.getAttribute("src") || "";
      if (!/^https?:\/\//i.test(src)) node.removeAttribute("src");
    }
    // Keep font-size formatting within a small, controlled range only.
    if (tag === "font") {
      const size = node.getAttribute("size") || "";
      if (!/^[1-5]$/.test(size)) node.removeAttribute("size");
    }
    Array.from(node.childNodes).forEach(clean);
  }
  Array.from(doc.body.childNodes).forEach(clean);
  return doc.body.innerHTML;
}

function isHtmlBody(body) {
  return typeof body === "string" && body.startsWith(HTML_PREFIX);
}
function getHtmlContent(body) {
  return isHtmlBody(body) ? body.slice(HTML_PREFIX.length) : null;
}
function plainTextToHtml(text = "") {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML.replace(/\r\n|\r|\n/g, "<br>");
}

// Images are rendered in published posts, but are converted back to an editable
// shortcode when a post is opened in the rich editor. This makes changing
// small/medium/large/full possible without having to delete and reinsert images.
function prepareHtmlForEditor(html = "") {
  const doc = new DOMParser().parseFromString(String(html), "text/html");
  Array.from(doc.querySelectorAll("img")).forEach(img => {
    const src = img.getAttribute("src") || "";
    if (!/^https?:\/\//i.test(src)) return;

    let size = "medium";
    if (img.classList.contains("fmd-image-small")) size = "small";
    else if (img.classList.contains("fmd-image-large")) size = "large";
    else if (img.classList.contains("fmd-image-full")) size = "full";

    img.replaceWith(doc.createTextNode(`[image:${size}](${src})`));
  });
  return doc.body.innerHTML;
}

// Render safe inline Markdown inside forum posts.
// Supports: **bold**, *italic*, ***bold italic***, ~~strikethrough~~,
// `inline code`, [links](https://example.com), [icon:name], and
// [download:Button label](https://example.com/file.jar), and resizable guide images.
// Images: [image](url), [image:small](url), [image:medium](url), [image:large](url), [image:full](url).
function renderInlineMarkdown(text = "") {
  const escapeHtml = value => String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#039;");

  const tokens = [];
  const stash = html => {
    const key = `\u0000MD${tokens.length}\u0000`;
    tokens.push(html);
    return key;
  };

  // Protect code first so Markdown inside `code` stays literal.
  let value = String(text).replace(/`([^`\n]+)`/g, (_, code) =>
    stash(`<code>${escapeHtml(code)}</code>`)
  );

  const ICON_NAMES = new Set([
    "monitor","clipboard","alert","download","package","folder","file","play",
    "settings","circle-help","refresh-cw","trash","life-buoy",
  ]);

  // Guide icon shortcode. Only a fixed allow-list is rendered, so arbitrary
  // classes cannot be injected through forum content.
  value = value.replace(/\[icon:([a-z0-9-]+)\]/gi, (match, rawName) => {
    const name = String(rawName).toLowerCase();
    return ICON_NAMES.has(name)
      ? stash(`<span class="fmd-icon fmd-icon-${name}" aria-hidden="true"></span>`)
      : match;
  });

  // Styled TOA download button. This is deliberately limited to http/https.
  value = value.replace(/\[download:([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)/gi, (_, label, href) =>
    stash(
      `<a class="fmd-download" href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">` +
      `<span class="fmd-icon fmd-icon-download" aria-hidden="true"></span>` +
      `<span>${escapeHtml(label)}</span></a>`
    )
  );

  // Guide image shortcode with optional sizing. Default is medium so screenshots
  // do not stretch across the entire post.
  value = value.replace(/\[image(?::(small|medium|large|full))?\]\((https?:\/\/[^\s)]+)\)/gi, (_, rawSize, src) => {
    const size = (rawSize || "medium").toLowerCase();
    return stash(`<img class="fmd-image fmd-image-${size}" src="${escapeHtml(src)}" alt="Guide image" loading="lazy" />`);
  });

  // Also support normal Markdown image syntax. It uses medium sizing by default.
  value = value.replace(/!\[([^\]\n]*)\]\((https?:\/\/[^\s)]+)\)/gi, (_, alt, src) =>
    stash(`<img class="fmd-image fmd-image-medium" src="${escapeHtml(src)}" alt="${escapeHtml(alt || "Guide image")}" loading="lazy" />`)
  );

  // Only allow normal http/https Markdown links.
  value = value.replace(/\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)/gi, (_, label, href) =>
    stash(`<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${escapeHtml(label)}</a>`)
  );

  value = escapeHtml(value);

  // Order matters: most specific first.
  value = value
    .replace(/\*\*\*([^*\n]+?)\*\*\*/g, "<strong><em>$1</em></strong>")
    .replace(/___([^_\n]+?)___/g, "<strong><em>$1</em></strong>")
    .replace(/\*\*([^*\n]+?)\*\*/g, "<strong>$1</strong>")
    .replace(/__([^_\n]+?)__/g, "<strong>$1</strong>")
    .replace(/~~([^~\n]+?)~~/g, "<s>$1</s>")
    .replace(/(^|[^*])\*([^*\n]+?)\*(?!\*)/g, "$1<em>$2</em>")
    .replace(/(^|[^_])_([^_\n]+?)_(?!_)/g, "$1<em>$2</em>");

  tokens.forEach((html, i) => {
    value = value.split(`\u0000MD${i}\u0000`).join(html);
  });

  return value;
}

// Render block-level Markdown used in forum posts while keeping the existing
// inline Markdown support. Supported here: headings, single > blockquotes,
// and simple unordered lists using either * item or - item.
function renderPlainPost(text = "") {
  const lines = String(text).replace(/\r\n?/g, "\n").split("\n");
  const out = [];
  let listItems = [];
  let quoteLines = [];

  const flushList = () => {
    if (!listItems.length) return;
    out.push(`<ul>${listItems.map(item => `<li>${renderInlineMarkdown(item)}</li>`).join("")}</ul>`);
    listItems = [];
  };

  const flushQuote = () => {
    if (!quoteLines.length) return;
    out.push(`<blockquote>${quoteLines.map(line => renderInlineMarkdown(line)).join("<br>")}</blockquote>`);
    quoteLines = [];
  };

  lines.forEach(line => {
    const heading = line.match(/^(#{1,3})[ \t]+(.+)$/);
    const quote = line.match(/^>[ \t]?(.*)$/);
    const bullet = line.match(/^[ \t]*[*-][ \t]+(.+)$/);

    if (quote) {
      flushList();
      quoteLines.push(quote[1].trim());
      return;
    }

    flushQuote();

    if (bullet) {
      listItems.push(bullet[1].trim());
      return;
    }

    flushList();

    if (heading) {
      const level = heading[1].length;
      out.push(`<h${level}>${renderInlineMarkdown(heading[2].trim())}</h${level}>`);
    } else {
      out.push(line ? `<div>${renderInlineMarkdown(line)}</div>` : "<br>");
    }
  });

  flushQuote();
  flushList();
  return out.join("");
}

function renderRichPost(html = "") {
  const safe = sanitizeHtml(html);
  const doc = new DOMParser().parseFromString(safe, "text/html");

  // Convert block Markdown created by typing/pasting into contentEditable.
  // This keeps Markdown-friendly authoring even though posts are stored as HTML.
  const convertBlocks = node => {
    if (!node || node.nodeType !== 1) return;
    const tag = node.tagName.toLowerCase();
    if ((tag === "div" || tag === "p") && !node.querySelector("div,p,h1,h2,h3,ul,ol,blockquote,pre")) {
      const text = node.textContent || "";
      const heading = text.match(/^\s*(#{1,3})[ \t]+(.+?)\s*$/);
      const quote = text.match(/^\s*>[ \t]?(.*?)\s*$/);
      const bullet = text.match(/^\s*[*-][ \t]+(.+?)\s*$/);

      if (heading) {
        const el = doc.createElement(`h${heading[1].length}`);
        el.innerHTML = renderInlineMarkdown(heading[2]);
        node.replaceWith(el);
        return;
      }
      if (quote) {
        const el = doc.createElement("blockquote");
        el.innerHTML = renderInlineMarkdown(quote[1]);
        node.replaceWith(el);
        return;
      }
      if (bullet) {
        const ul = doc.createElement("ul");
        const li = doc.createElement("li");
        li.innerHTML = renderInlineMarkdown(bullet[1]);
        ul.appendChild(li);
        node.replaceWith(ul);
        return;
      }
    }
    Array.from(node.children).forEach(convertBlocks);
  };

  Array.from(doc.body.children).forEach(convertBlocks);

  // Merge Markdown bullet lines into one compact list. contentEditable often
  // inserts <br> or empty <div>/<p> nodes between pasted lines, so treat those
  // as separators rather than as real paragraph breaks.
  const isTrivialListSeparator = el => {
    if (!el || el.nodeType !== 1) return false;
    const tag = el.tagName.toLowerCase();
    if (tag === "br") return true;
    if (tag !== "div" && tag !== "p") return false;
    return !(el.textContent || "").trim() && !el.querySelector("img,a,code,pre,blockquote,ul,ol");
  };

  const mergeBulletLists = () => {
    Array.from(doc.body.children).forEach(node => {
      if (node.tagName?.toLowerCase() !== "ul") return;
      let cursor = node.nextElementSibling;
      while (cursor) {
        const separators = [];
        while (cursor && isTrivialListSeparator(cursor)) {
          separators.push(cursor);
          cursor = cursor.nextElementSibling;
        }
        if (!cursor || cursor.tagName?.toLowerCase() !== "ul") break;
        separators.forEach(el => el.remove());
        const after = cursor.nextElementSibling;
        Array.from(cursor.children).forEach(li => node.appendChild(li));
        cursor.remove();
        cursor = after;
      }
    });
  };

  mergeBulletLists();

  // Merge adjacent blockquotes created from consecutive Markdown `>` lines.
  // This makes:
  // > Important:
  // > More detail
  // render as one callout instead of two separated purple boxes.
  Array.from(doc.body.children).forEach(node => {
    if (node.tagName?.toLowerCase() !== "blockquote") return;
    let next = node.nextElementSibling;
    while (next && next.tagName?.toLowerCase() === "blockquote") {
      const after = next.nextElementSibling;
      node.appendChild(doc.createElement("br"));
      while (next.firstChild) node.appendChild(next.firstChild);
      next.remove();
      next = after;
    }
  });

  // contentEditable can also produce a bare text node followed by <br>.
  Array.from(doc.body.childNodes).forEach(node => {
    if (node.nodeType !== 3) return;
    const text = node.textContent || "";
    const heading = text.match(/^\s*(#{1,3})[ \t]+(.+?)\s*$/);
    const quote = text.match(/^\s*>[ \t]?(.*?)\s*$/);
    const bullet = text.match(/^\s*[*-][ \t]+(.+?)\s*$/);
    if (heading) {
      const el = doc.createElement(`h${heading[1].length}`);
      el.innerHTML = renderInlineMarkdown(heading[2]);
      node.replaceWith(el);
    } else if (quote) {
      const el = doc.createElement("blockquote");
      el.innerHTML = renderInlineMarkdown(quote[1]);
      node.replaceWith(el);
    } else if (bullet) {
      const ul = doc.createElement("ul");
      const li = doc.createElement("li");
      li.innerHTML = renderInlineMarkdown(bullet[1]);
      ul.appendChild(li);
      node.replaceWith(ul);
    }
  });

  // Bare text-node bullets above can also create one-item lists. Merge again
  // after those conversions using the same separator-tolerant logic.
  mergeBulletLists();

  // Convert inline Markdown in text nodes, including admin/rich-editor posts.
  // Skip elements where Markdown should stay literal or that are already formatted.
  const SKIP_INLINE = new Set(["code", "pre", "a", "strong", "b", "em", "i", "s"]);

  const convertInlineText = node => {
    if (!node) return;

    if (node.nodeType === 1) {
      const tag = node.tagName.toLowerCase();
      if (SKIP_INLINE.has(tag)) return;
      Array.from(node.childNodes).forEach(convertInlineText);
      return;
    }

    if (node.nodeType !== 3) return;
    const raw = node.nodeValue || "";
    if (!/(\*\*|\*|__|_|~~|`|\[icon:|\[download:|\[image(?::(?:small|medium|large|full))?\]|!?\[[^\]]+\]\(https?:\/\/)/i.test(raw)) return;

    const rendered = renderInlineMarkdown(raw);
    if (rendered === raw) return;

    const template = doc.createElement("template");
    template.innerHTML = rendered;
    node.replaceWith(template.content.cloneNode(true));
  };

  Array.from(doc.body.childNodes).forEach(convertInlineText);

  // Sanitize once more because this function creates formatting elements itself.
  return sanitizeHtml(doc.body.innerHTML);
}

// ── Rich Editor ───────────────────────────────────────────────────────────────
function RichEditor({ value, onChange, placeholder = "Write your post…" }) {
  const editorRef  = useRef(null);
  const savedRange = useRef(null);
  const [linkOpen, setLinkOpen]   = useState(false);
  const [imgOpen,  setImgOpen]    = useState(false);
  const [linkVal,  setLinkVal]    = useState("");
  const [imgVal,   setImgVal]     = useState("");
  const [activeFormats, setActiveFormats] = useState({});

  // Initialise editor content only once
  useEffect(() => {
    if (!editorRef.current) return;
    const html = isHtmlBody(value)
      ? prepareHtmlForEditor(getHtmlContent(value) || "")
      : plainTextToHtml(value || "");
    if (editorRef.current.innerHTML !== html) {
      editorRef.current.innerHTML = html || "";
    }
  }, []); // intentional: only run once on mount to set initial HTML content

  const saveRange = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) savedRange.current = sel.getRangeAt(0).cloneRange();
  };
  const restoreRange = () => {
    const sel = window.getSelection();
    if (savedRange.current && sel) {
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    }
  };

  const emit = () => {
    if (!editorRef.current) return;
    const html = editorRef.current.innerHTML;
    // If editor is effectively empty, send empty string
    const stripped = html.replace(/<[^>]*>/g, "").trim();
    onChange(stripped ? HTML_PREFIX + html : "");
  };

  const updateActiveFormats = () => {
    setActiveFormats({
      bold:      document.queryCommandState("bold"),
      italic:    document.queryCommandState("italic"),
      underline: document.queryCommandState("underline"),
      ul:        document.queryCommandState("insertUnorderedList"),
      ol:        document.queryCommandState("insertOrderedList"),
    });
  };

  const exec = (cmd, val) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val ?? null);
    emit();
    updateActiveFormats();
  };

  const insertHeading = (tag) => {
    editorRef.current?.focus();
    document.execCommand("formatBlock", false, tag);
    emit();
  };

  const setTextSize = (size) => {
    restoreRange();
    editorRef.current?.focus();
    document.execCommand("fontSize", false, String(size));
    emit();
  };

  const insertHr = () => {
    editorRef.current?.focus();
    document.execCommand("insertHTML", false, "<hr/>");
    emit();
  };

  const insertCode = () => {
    editorRef.current?.focus();
    const sel = window.getSelection();
    const selected = sel && sel.rangeCount > 0 ? sel.getRangeAt(0).toString() : "";
    if (selected) {
      document.execCommand("insertHTML", false, `<code>${selected}</code>`);
    } else {
      document.execCommand("insertHTML", false, "<pre><code>code here</code></pre>");
    }
    emit();
  };

  const insertQuote = () => {
    editorRef.current?.focus();
    document.execCommand("formatBlock", false, "blockquote");
    emit();
  };

  const applyLink = () => {
    restoreRange();
    if (linkVal.trim()) {
      const href = /^https?:\/\//i.test(linkVal.trim()) ? linkVal.trim() : "https://" + linkVal.trim();
      document.execCommand("createLink", false, href);
    }
    setLinkOpen(false); setLinkVal("");
    emit();
  };

  const applyImg = () => {
    restoreRange();
    if (imgVal.trim()) {
      document.execCommand("insertHTML", false,
        `<img class="fmd-image fmd-image-medium" src="${imgVal.trim()}" alt="image" />`);
    }
    setImgOpen(false); setImgVal("");
    emit();
  };

  const TBtn = ({ cmd, title, children, active }) => (
    <button type="button" className={`ftb${active || activeFormats[cmd] ? " active" : ""}`}
      title={title}
      onMouseDown={e => { e.preventDefault(); saveRange(); exec(cmd); }}>
      {children}
    </button>
  );

  return (
    <div className="frich-wrap">
      <div className="frich-toolbar">
        {/* Text style */}
        <TBtn cmd="bold"      title="Bold (Ctrl+B)"><strong>B</strong></TBtn>
        <TBtn cmd="italic"    title="Italic (Ctrl+I)"><em>I</em></TBtn>
        <TBtn cmd="underline" title="Underline (Ctrl+U)"><u>U</u></TBtn>
        <div className="frich-sep" />
        {/* Paragraph / headings */}
        <button type="button" className="ftb" title="Normal paragraph"
          onMouseDown={e=>{ e.preventDefault(); insertHeading("p"); }}>P</button>
        <button type="button" className="ftb" title="Heading 1"
          onMouseDown={e=>{ e.preventDefault(); insertHeading("h1"); }}>H1</button>
        <button type="button" className="ftb" title="Heading 2"
          onMouseDown={e=>{ e.preventDefault(); insertHeading("h2"); }}>H2</button>
        <button type="button" className="ftb" title="Heading 3"
          onMouseDown={e=>{ e.preventDefault(); insertHeading("h3"); }}>H3</button>
        <div className="frich-sep" />
        {/* Font size */}
        <button type="button" className="ftb" title="Small text"
          onMouseDown={e=>{ e.preventDefault(); saveRange(); setTextSize(2); }} style={{fontSize:10}}>A−</button>
        <button type="button" className="ftb" title="Normal text size"
          onMouseDown={e=>{ e.preventDefault(); saveRange(); setTextSize(3); }}>A</button>
        <button type="button" className="ftb" title="Large text"
          onMouseDown={e=>{ e.preventDefault(); saveRange(); setTextSize(4); }} style={{fontSize:14}}>A+</button>
        <button type="button" className="ftb" title="Extra large text"
          onMouseDown={e=>{ e.preventDefault(); saveRange(); setTextSize(5); }} style={{fontSize:16}}>A++</button>
        <div className="frich-sep" />
        {/* Lists */}
        <TBtn cmd="insertUnorderedList" title="Bullet list" active={activeFormats.ul}>
          <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor">
            <circle cx="2" cy="4" r="1.5"/><rect x="5" y="3" width="11" height="2" rx="1"/>
            <circle cx="2" cy="8" r="1.5"/><rect x="5" y="7" width="11" height="2" rx="1"/>
            <circle cx="2" cy="12" r="1.5"/><rect x="5" y="11" width="11" height="2" rx="1"/>
          </svg>
        </TBtn>
        <TBtn cmd="insertOrderedList" title="Numbered list" active={activeFormats.ol}>
          <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor">
            <text x="0" y="5" fontSize="5" fontFamily="monospace">1.</text>
            <rect x="5" y="3" width="11" height="2" rx="1"/>
            <text x="0" y="10" fontSize="5" fontFamily="monospace">2.</text>
            <rect x="5" y="7" width="11" height="2" rx="1"/>
          </svg>
        </TBtn>
        <div className="frich-sep" />
        {/* Block elements */}
        <button type="button" className="ftb" title="Blockquote"
          onMouseDown={e=>{ e.preventDefault(); insertQuote(); }}>❝</button>
        <button type="button" className="ftb" title="Code"
          onMouseDown={e=>{ e.preventDefault(); insertCode(); }}>
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <polyline points="5,4 1,8 5,12"/><polyline points="11,4 15,8 11,12"/>
          </svg>
        </button>
        <button type="button" className="ftb" title="Horizontal rule"
          onMouseDown={e=>{ e.preventDefault(); insertHr(); }}>—</button>
        <div className="frich-sep" />
        {/* Link & image */}
        <button type="button" className={`ftb${linkOpen?" active":""}`} title="Insert link"
          onMouseDown={e=>{ e.preventDefault(); saveRange(); setImgOpen(false); setLinkOpen(o=>!o); }}>
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <path d="M6 10a4 4 0 1 0 4-4M10 6a4 4 0 1 0-4 4"/>
            <line x1="6.5" y1="9.5" x2="9.5" y2="6.5"/>
          </svg>
        </button>
        <button type="button" className={`ftb${imgOpen?" active":""}`} title="Insert image URL"
          onMouseDown={e=>{ e.preventDefault(); saveRange(); setLinkOpen(false); setImgOpen(o=>!o); }}>
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <rect x="1" y="2" width="14" height="12" rx="1.5"/>
            <circle cx="5.5" cy="6" r="1.5"/>
            <path d="M1 11l4-4 3 3 2-2 5 5"/>
          </svg>
        </button>
      </div>

      {linkOpen && (
        <div className="frich-link-row">
          <input autoFocus placeholder="https://…" value={linkVal}
            onChange={e=>setLinkVal(e.target.value)}
            onKeyDown={e=>{ if(e.key==="Enter"){ e.preventDefault(); applyLink(); } if(e.key==="Escape") setLinkOpen(false); }} />
          <button type="button" className="fbtn fbtn-p" style={{ padding:"5px 12px",fontSize:10 }}
            onClick={applyLink}>Insert</button>
          <button type="button" className="fbtn fbtn-g" style={{ padding:"5px 10px",fontSize:10 }}
            onClick={()=>setLinkOpen(false)}>✕</button>
        </div>
      )}

      {imgOpen && (
        <div className="frich-img-row">
          <input autoFocus placeholder="https://i.imgur.com/example.jpg" value={imgVal}
            onChange={e=>setImgVal(e.target.value)}
            onKeyDown={e=>{ if(e.key==="Enter"){ e.preventDefault(); applyImg(); } if(e.key==="Escape") setImgOpen(false); }} />
          <button type="button" className="fbtn fbtn-p" style={{ padding:"5px 12px",fontSize:10 }}
            onClick={applyImg}>Insert</button>
          <button type="button" className="fbtn fbtn-g" style={{ padding:"5px 10px",fontSize:10 }}
            onClick={()=>setImgOpen(false)}>✕</button>
        </div>
      )}

      <div
        ref={editorRef}
        className="frich-editor"
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder}
        onInput={()=>{ emit(); updateActiveFormats(); }}
        onKeyUp={updateActiveFormats}
        onMouseUp={updateActiveFormats}
        onPaste={e => {
          // Never let the browser's default rich/HTML paste through — it
          // splits multi-line plain text into one <div> per line (or, for
          // genuinely rich clipboard content, can carry structural tags).
          // Always paste as plain text instead, inserted as a single node.
          e.preventDefault();
          const text = e.clipboardData.getData("text/plain");
          if (!text) return;

          const sel = window.getSelection();
          if (!sel || sel.rangeCount === 0) return;
          const range = sel.getRangeAt(0);
          range.deleteContents();

          // Insert as text, converting newlines to <br> so multi-line
          // pastes still read naturally without becoming separate blocks.
          const lines = text.split(/\r\n|\r|\n/);
          const frag = document.createDocumentFragment();
          lines.forEach((line, i) => {
            frag.appendChild(document.createTextNode(line));
            if (i < lines.length - 1) frag.appendChild(document.createElement("br"));
          });
          const lastNode = frag.lastChild;
          range.insertNode(frag);

          // Move caret to just after the inserted content
          if (lastNode) {
            const newRange = document.createRange();
            newRange.setStartAfter(lastNode);
            newRange.collapse(true);
            sel.removeAllRanges();
            sel.addRange(newRange);
          }

          emit();
          updateActiveFormats();
        }}
        onKeyDown={e => {
          // Ctrl+Enter submit bubbles up via a custom event — handled by parent
          if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
            e.currentTarget.dispatchEvent(new CustomEvent("richsubmit", { bubbles: true }));
          }
        }}
      />
    </div>
  );
}

function MCGrassIcon({ size = 20, color = "#4ade80" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none"
      style={{ display:"block", imageRendering:"pixelated" }}>
      <rect x="0" y="0" width="20" height="20" rx="1"
        fill="none" stroke={color} strokeWidth="1.2" opacity="0.5"/>
      <rect x="2" y="2" width="16" height="16" fill={color} opacity="0.15"/>
      <rect x="4" y="6" width="4" height="4" fill={color} opacity="0.9"/>
      <rect x="12" y="6" width="4" height="4" fill={color} opacity="0.9"/>
      <rect x="8"  y="12" width="4" height="2" fill={color} opacity="0.9"/>
      <rect x="6"  y="12" width="2" height="4" fill={color} opacity="0.9"/>
      <rect x="12" y="12" width="2" height="4" fill={color} opacity="0.9"/>
      <rect x="8"  y="14" width="4" height="2" fill={color} opacity="0.4"/>
    </svg>
  );
}

function ReligionIcon({ size = 17, color = "#e2b96a" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" style={{ display:"block" }}>
      <path d="M12 3a9 9 0 1 0 9 9 6.5 6.5 0 0 1-9-9z"/>
      <polygon points="17,5 17.9,7.6 20.7,7.6 18.5,9.3 19.3,12 17,10.4 14.7,12 15.5,9.3 13.3,7.6 16.1,7.6"/>
    </svg>
  );
}

const INFO_SUBCATS = [
  { id:"announcements",  label:"Announcements",      desc:"Major news and official statements from staff.", icon:Megaphone,   color:"#9b7fd4" },
  { id:"rules",          label:"Rules & Guidelines", desc:"Community rules, conduct policy and terms.",     icon:ShieldAlert, color:"#f87171" },
  { id:"general-guides", label:"Resources",          desc:"General how-to guides, FAQs and resources.",    icon:BookOpen,    color:"#4ade80" },
];

const COMMUNITY_SUBCATS = [
  // General posts are shown automatically on the Community Discussion page,
  // so there is no separate General Discussion section here.
  { id:"fitness",            label:"Fitness",            desc:"Workout logs, routines, nutrition and health.",       icon:Dumbbell,      color:"#f87171" },
  { id:"religion",           label:"Religion & Faith",   desc:"Faith, philosophy, theology and spiritual topics.",   icon:null, isReligion:true, color:"#e2b96a" },
];

// mc-general is a hidden internal tag used when posting from the Minecraft main page.
// It is not listed in MC_SUBCATS but posts tagged mc-general show in the General Posts section.
const MC_SUBCATS = [
  { id:"mc-updates",  label:"Updates & Patch Notes", desc:"Latest server changes, features and version notes.", icon:Wrench,    color:"#c9a84c" },
  { id:"mc-guides",   label:"Guides & Tutorials",    desc:"Server guides, base building tips and tutorials.",   icon:Map,       color:"#60a5fa" },
  { id:"suggestions", label:"Suggestions",           desc:"Share ideas and suggestions to improve the server.", icon:Megaphone, color:"#f59e0b" },
  { id:"factions",    label:"Factions",              desc:"Faction wars, alliances, diplomacy and politics.",   icon:Swords,    color:"#4ade80" },
];

const CATS = [
  { id:"information", label:"Information",          desc:"Announcements, rules and general community resources.", color:"#c9a84c", icon:FileText,      locked:true,  hasSubcats:true, subcats:INFO_SUBCATS },
  // defaultTag:"general-discussion" — posts from main Community page go here (hidden from subcat list)
  { id:"community",   label:"Community Discussion", desc:"Open discussion — post freely or browse by topic.",     color:"#9b7fd4", icon:MessageCircle, locked:false, hasSubcats:true, defaultTag:"general-discussion", subcats:COMMUNITY_SUBCATS },
  // defaultTag:"mc-other" — posts from main Minecraft page get tagged mc-general (hidden from subcat list)
  { id:"minecraft",   label:"Minecraft",            desc:"Factions, events, base showcases and server updates.",  color:"#4ade80", isMC:true,          locked:false, hasSubcats:true, defaultTag:"mc-other", subcats:MC_SUBCATS },
];

function rel(d) {
  const s=Math.floor((Date.now()-new Date(d))/1000);
  if(s<60) return "just now";
  const m=Math.floor(s/60); if(m<60) return `${m}m ago`;
  const h=Math.floor(m/60); if(h<24) return `${h}h ago`;
  const dy=Math.floor(h/24); if(dy<30) return `${dy}d ago`;
  return new Date(d).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"});
}

const isMod = u => u && ['admin','moderator'].includes(u.role);

function Avatar({ size=34, role, photoUrl }) {
  const c = role==="admin"?"#c9a84c":role==="moderator"?"#9b7fd4":"#4a3870";
  const [imgErr, setImgErr] = useState(false);
  const showPhoto = photoUrl && !imgErr;
  return (
    <div style={{ width:size,height:size,borderRadius:"50%",flexShrink:0,
      background:`${c}18`,border:`1.5px solid ${c}40`,
      display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden" }}>
      {showPhoto
        ? <img src={photoUrl} alt="avatar" onError={()=>setImgErr(true)}
            style={{ width:"100%",height:"100%",objectFit:"cover",borderRadius:"50%" }} />
        : <User style={{ width:size*.42,height:size*.42,color:`${c}bb` }} />}
    </div>
  );
}

function RolePill({ role }) {
  const m = {
    admin:     { c:"#c9a84c", bg:"rgba(201,168,76,.1)",  b:"rgba(201,168,76,.25)",  l:"Admin" },
    moderator: { c:"#9b7fd4", bg:"rgba(123,94,167,.1)",  b:"rgba(123,94,167,.25)",  l:"Mod"   },
    vip:       { c:"#4ade80", bg:"rgba(74,222,128,.08)", b:"rgba(74,222,128,.22)",  l:"VIP"   },
  }[role];
  if(!m) return null;
  return <span style={{ fontSize:9,letterSpacing:1,padding:"2px 7px",borderRadius:4,fontWeight:700,
    fontFamily:"'Cinzel',serif",color:m.c,background:m.bg,border:`1px solid ${m.b}` }}>{m.l}</span>;
}

function Tag({ label, color }) {
  return (
    <span style={{ fontSize:9,letterSpacing:1.5,padding:"2px 8px",borderRadius:4,
      fontFamily:"'Cinzel',serif",fontWeight:700,textTransform:"uppercase",
      color, background:`${color}14`, border:`1px solid ${color}28` }}>{label}</span>
  );
}

function VoteWidget({ count=0, userVote=0, onUp, onDown, disabled=false }) {
  const up   = userVote===1;
  const down = userVote===-1;
  const color = up?"#f59e0b":down?"#9b7fd4":"#4a3870";
  const fmt  = n=>n>=1000?(n/1000).toFixed(1)+"k":String(n);
  return (
    <div className="fvote" onClick={e=>e.stopPropagation()}>
      <button className="fvote-btn" disabled={disabled} onClick={onUp}
        style={{ color:up?"#f59e0b":"#4a3870" }}>
        <svg width={10} height={10} viewBox="0 0 12 12" fill="currentColor">
          <path d="M6 1L11.5 9H.5L6 1Z"/>
        </svg>
      </button>
      <span className="fvote-count" style={{ color }}>{fmt(count)}</span>
      <button className="fvote-btn" disabled={disabled} onClick={onDown}
        style={{ color:down?"#9b7fd4":"#4a3870" }}>
        <svg width={10} height={10} viewBox="0 0 12 12" fill="currentColor">
          <path d="M6 11L.5 3H11.5L6 11Z"/>
        </svg>
      </button>
    </div>
  );
}

function CreateBar({ onNew, authUser }) {
  if(!authUser) return null;
  return (
    <div className="fcreate-bar">
      <Avatar size={34} role={authUser.role} photoUrl={authUser.photo_url||authUser.photoUrl||authUser.avatar} />
      <div className="fcreate-fake-input" role="button" tabIndex={0}
        onClick={onNew} onKeyDown={e=>e.key==="Enter"&&onNew()}>
        Create a post…
      </div>
      <button className="fbtn fbtn-p" style={{ padding:"7px 16px",flexShrink:0 }} onClick={onNew}>
        <Plus style={{ width:11,height:11 }} /> Post
      </button>
    </div>
  );
}

function Skel({ w="100%", h=13, mb=8 }) {
  return <div className="fsk" style={{ width:w,height:h,marginBottom:mb }} />;
}

function ColHeader({ cols, arrowOffset=0 }) {
  return (
    <div style={{ display:"flex",padding:"10px 24px",borderBottom:"1px solid rgba(42,32,64,.6)",
      background:"rgba(255,255,255,.018)",paddingRight:arrowOffset?`${24+arrowOffset}px`:"24px" }}>
      {cols.map(([label,w,align],i)=>(
        <span key={i} className={i>0?"fhide":""} style={{ width:w||"auto",flex:w?"none":1,fontSize:10,fontWeight:600,
          letterSpacing:1.5,textTransform:"uppercase",color:"#6b6080",
          fontFamily:"'Cinzel',serif",textAlign:align||"left",flexShrink:0 }}>{label}</span>
      ))}
    </div>
  );
}

function ForumPageCanvas() {
  const ref = useRef(null);
  useEffect(()=>{
    const pc = ref.current; if(!pc) return;
    const ctx = pc.getContext('2d');
    const resize = ()=>{ pc.width=window.innerWidth; pc.height=window.innerHeight; };
    resize();
    const particles = [];
    for(let i=0;i<80;i++){
      particles.push({
        x:Math.random()*window.innerWidth, y:Math.random()*window.innerHeight,
        r:Math.random()*1.6+0.3, op:Math.random()*.45+.08,
        life:Math.random(), speed:Math.random()*.0025+.0008,
        drift:Math.random()*6-3, color:Math.random()<.6?'#c9a84c':'#9b7fd4',
      });
    }
    let raf;
    function draw(){
      ctx.clearRect(0,0,pc.width,pc.height);
      particles.forEach(p=>{
        p.life+=p.speed;
        if(p.life>1){ p.life=0; p.x=Math.random()*pc.width; p.y=pc.height+10; }
        const t=Math.sin(p.life*Math.PI);
        const x=p.x+Math.sin(p.life*5+p.drift)*10;
        const y=pc.height - p.life*pc.height;
        ctx.save(); ctx.globalAlpha=t*p.op; ctx.fillStyle=p.color;
        ctx.shadowColor=p.color; ctx.shadowBlur=8;
        ctx.beginPath(); ctx.arc(x,y,p.r,0,Math.PI*2); ctx.fill(); ctx.restore();
      });
      raf=requestAnimationFrame(draw);
    }
    draw();
    window.addEventListener('resize',resize);
    return ()=>{ cancelAnimationFrame(raf); window.removeEventListener('resize',resize); };
  },[]);
  return <canvas ref={ref} style={{ position:'fixed',inset:0,width:'100%',height:'100%',pointerEvents:'none',zIndex:1,display:'block' }} />;
}

function ForumHero() {
  return (
    <div style={{ position:"relative",textAlign:"center",padding:"64px 32px 48px",overflow:"hidden",borderBottom:"1px solid rgba(42,32,64,.7)" }}>
      <div style={{ position:"relative",zIndex:2 }}>
        <h1 style={{
          fontFamily:"'Cinzel Decorative',serif", fontSize:"clamp(32px,6vw,64px)",
          fontWeight:900, lineHeight:1.05,
          background:"linear-gradient(140deg,#fff 0%,#e8c97a 35%,#9b7fd4 65%,#d0c0ff 100%)",
          WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text",
          filter:"drop-shadow(0 0 50px rgba(123,94,167,.6))",
          margin:"0 auto 14px", letterSpacing:2,
        }}>TOA Forums</h1>
        <p style={{ fontFamily:"'Cinzel',serif",fontSize:"clamp(11px,1.6vw,14px)",color:"#a89cc8",letterSpacing:3,fontStyle:"italic",margin:0,opacity:.85 }}>
          <em style={{ color:"#c9a84c",fontStyle:"normal" }}>Rise Above.</em> Grow Together.
        </p>
      </div>
    </div>
  );
}

// ── Home component ────────────────────────────────────────────────────────────
// Behaviour per category on the home page:
//
//  • Information  — fetch 3 recents per subcat, show per subcat row (unchanged)
//  • Community    — fetch 1 recent from the whole category (unfiltered), show
//                   once under the category row. No recents inside subcat rows.
//  • Minecraft    — fetch 3 recents from the whole category (unfiltered), show
//                   them directly under the category row. No subcat-level recents.
//
function Home({ onSelect, onSubcat, stats, statsLoading, onArchives, authUser }) {
  // Per-subcat recents (used only for Information)
  const [recentSubcat, setRecentSubcat] = useState({});
  const [loadingSubcat, setLoadingSubcat] = useState({});
  // Per-category recents (used for Community [limit 1] and Minecraft [limit 3])
  const [recentCat, setRecentCat] = useState({});
  const [loadingCat, setLoadingCat] = useState({});

  useEffect(()=>{
    // Information — per subcat, 1 recent each
    INFO_SUBCATS.forEach(sub=>{
      setLoadingSubcat(p=>({...p,[sub.id]:true}));
      fetch(`${API}/forum/categories/information/threads?page=1&limit=1&tag=${sub.id}`)
        .then(r=>r.json()).then(d=>setRecentSubcat(p=>({...p,[sub.id]:d.threads||[]})))
        .catch(()=>setRecentSubcat(p=>({...p,[sub.id]:[]})))
        .finally(()=>setLoadingSubcat(p=>({...p,[sub.id]:false})));
    });

    // Community — 1 most recent from whole category (no tag filter)
    setLoadingCat(p=>({...p,community:true}));
    fetch(`${API}/forum/categories/community/threads?page=1&limit=1`)
      .then(r=>r.json()).then(d=>setRecentCat(p=>({...p,community:d.threads||[]})))
      .catch(()=>setRecentCat(p=>({...p,community:[]})))
      .finally(()=>setLoadingCat(p=>({...p,community:false})));

    // Minecraft — 1 most recent from whole category (no tag filter)
    setLoadingCat(p=>({...p,minecraft:true}));
    fetch(`${API}/forum/categories/minecraft/threads?page=1&limit=1`)
      .then(r=>r.json()).then(d=>setRecentCat(p=>({...p,minecraft:d.threads||[]})))
      .catch(()=>setRecentCat(p=>({...p,minecraft:[]})))
      .finally(()=>setLoadingCat(p=>({...p,minecraft:false})));
  },[]);

  return (
    <div className="ff">
      <ForumHero />
      <ColHeader cols={[["Category"],["Threads","72px","center"],["Comments","82px","center"]]} arrowOffset={34} />
      {isMod(authUser) && (
        <div style={{ padding:"8px 24px",borderBottom:"1px solid rgba(42,32,64,.4)",
          background:"rgba(245,158,11,.03)",display:"flex",alignItems:"center",gap:10 }}>
          <Archive style={{ width:12,height:12,color:"#f59e0b" }} />
          <span style={{ fontSize:12,color:"#6b6080" }}>Moderator tools:</span>
          <button className="fcrumb" style={{ color:"#f59e0b",fontSize:12 }} onClick={onArchives}>View Archives</button>
        </div>
      )}

      {CATS.map((cat,idx)=>{
        const Icon = cat.icon;
        const cs = stats?.categories?.[cat.id]||{threads:0,posts:0,lastPost:null};
        const isInfo      = cat.id==="information";
        const isCommunity = cat.id==="community";
        const isMC        = cat.id==="minecraft";

        return (
          <div key={cat.id} className={`fa fa${Math.min(idx+1,6)}`}>
            {/* ── Main category row ── */}
            <button className="fcat-row" onClick={()=>onSelect(cat.id)}>
              <div style={{ width:2,alignSelf:"stretch",background:cat.color,opacity:.5,flexShrink:0 }} />
              <div className="fcat-icon" style={{ width:36,height:36,flexShrink:0,margin:"18px 16px",
                display:"flex",alignItems:"center",justifyContent:"center",
                background:`${cat.color}12`,borderRadius:8,border:`1px solid ${cat.color}22` }}>
                {cat.isMC
                  ? <MCGrassIcon size={22} color={cat.color} />
                  : cat.isReligion
                  ? <ReligionIcon size={19} color={cat.color} />
                  : <Icon style={{ width:17,height:17,color:cat.color,opacity:.85 }} />}
              </div>
              <div style={{ flex:1,minWidth:0,padding:"16px 0",textAlign:"left" }}>
                <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap" }}>
                  <span className="fcat-label" style={{ fontFamily:"'Cinzel',serif",fontSize:14,fontWeight:700,color:"#e8e0f0",letterSpacing:.3 }}>{cat.label}</span>
                  {cat.locked && <Lock style={{ width:10,height:10,color:"#6b6080" }} />}
                </div>
                <p className="fcat-desc" style={{ fontSize:12.5,color:"#6b6080",margin:0,lineHeight:1.45 }}>{cat.desc}</p>
              </div>
              <div className="fhide" style={{ width:72,textAlign:"center",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center" }}>
                <span style={{ fontFamily:"'Cinzel Decorative',serif",fontSize:16,color:"#a89cc8",lineHeight:1 }}>
                  {statsLoading?"—":cs.threads??0}</span>
              </div>
              <div className="fhide" style={{ width:82,textAlign:"center",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center" }}>
                <span style={{ fontFamily:"'Cinzel Decorative',serif",fontSize:16,color:"#a89cc8",lineHeight:1 }}>
                  {statsLoading?"—":cs.posts??0}</span>
              </div>
              <ChevronRight className="fcat-arrow" style={{ width:14,height:14,alignSelf:"center" }} />
            </button>

            {/* ── Subcategory rows ── */}
            {cat.hasSubcats && (cat.subcats||[])
              // Community already has a General Posts area inside its landing
              // page, so do not duplicate General Discussion on forum home.
              .filter(sub => !(isCommunity && sub.isGeneralAlias))
              .map(sub=>{
              const SIcon = sub.icon;
              // Information: show per-subcat recents. Community & Minecraft: no recents inside subcat rows.
              const showSubRecents = isInfo;
              const recentSub = showSubRecents ? (recentSubcat[sub.id]||[]) : [];
              const loadingSub = showSubRecents ? (loadingSubcat[sub.id]||false) : false;

              return (
                <div key={sub.id}>
                  <button className="fsub-row"
                    onClick={e=>{e.stopPropagation();onSubcat(cat.id,sub.id);}}>
                    <div style={{ width:5,height:5,borderRadius:"50%",background:sub.color,flexShrink:0,opacity:.6 }} />
                    {sub.isReligion
                      ? <ReligionIcon size={13} color={sub.color} />
                      : SIcon ? <SIcon style={{ width:13,height:13,color:sub.color,flexShrink:0,opacity:.7 }} /> : null}
                    <span className="fsub-label" style={{ fontSize:12.5,color:"#a89cc8",fontWeight:500,minWidth:0 }}>{sub.label}</span>
                    <span className="fsub-desc" style={{ fontSize:11.5,color:"#6b6080",flex:1,textAlign:"left",
                      whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis" }}>
                      — {sub.desc}
                    </span>
                    <ChevronRight style={{ width:12,height:12,color:"#4a3870",flexShrink:0 }} />
                  </button>

                  {/* Per-subcat recent threads (Information only) */}
                  {loadingSub && (
                    <div style={{ padding:"6px 24px 6px 52px" }}>
                      <Skel w="50%" h={9} mb={0} />
                    </div>
                  )}
                  {!loadingSub && recentSub.map(t=>(
                    <button key={t.id} className="fmini-row" style={{ paddingLeft:52 }}
                      onClick={e=>{e.stopPropagation();onSelect(cat.id,threadUrlRef(t));}}>
                      <div style={{ width:3,height:3,borderRadius:"50%",background:sub.color,opacity:.5,flexShrink:0 }} />
                      <span style={{ flex:1,minWidth:0,fontSize:12,color:"#a89cc8",
                        whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis" }}>{t.title}</span>
                      <span style={{ fontSize:10,color:"#6b6080",flexShrink:0,display:"flex",alignItems:"center",gap:3 }}>
                        <MessageCircle style={{ width:8,height:8 }}/>{t.reply_count??0}
                      </span>
                      <span style={{ fontSize:10,color:"rgba(107,96,128,.45)",flexShrink:0 }}>
                        {rel(t.last_post_at||t.created_at)}
                      </span>
                    </button>
                  ))}
                </div>
              );
            })}

            {/* ── Category-level recents (Community: 1, Minecraft: 3) ── */}
            {(isCommunity || isMC) && (() => {
              const catRecents = recentCat[cat.id]||[];
              const catLoading = loadingCat[cat.id]||false;
              return (
                <>
                  {catLoading && (
                    <div style={{ padding:"6px 24px 6px 28px" }}>
                      <Skel w="50%" h={9} mb={0} />
                    </div>
                  )}
                  {!catLoading && catRecents.length>0 && (
                    <div style={{ padding:"5px 24px 2px 28px",display:"flex",alignItems:"center",gap:6 }}>
                      <Clock style={{ width:9,height:9,color:"#4a3870" }}/>
                      <span style={{ fontSize:9,fontFamily:"'Cinzel',serif",letterSpacing:1.5,
                        textTransform:"uppercase",color:"#4a3870",fontWeight:600 }}>Recent</span>
                    </div>
                  )}
                  {!catLoading && catRecents.map(t=>{
                    const allSubs=[...INFO_SUBCATS,...COMMUNITY_SUBCATS,...MC_SUBCATS];
                    const tagSub = t.tag ? allSubs.find(s=>s.id===t.tag) : null;
                    return (
                    <button key={t.id} className="fmini-row" style={{ paddingLeft:28 }}
                      onClick={e=>{e.stopPropagation();onSelect(cat.id,threadUrlRef(t));}}>
                      <div style={{ width:5,height:5,borderRadius:"50%",background:cat.color,opacity:.6,flexShrink:0 }} />
                      <div style={{ flex:1,minWidth:0,display:"flex",alignItems:"center",gap:7,overflow:"hidden" }}>
                        {tagSub && (
                          <span style={{ fontSize:11,color:"#6b6080",flexShrink:0,whiteSpace:"nowrap" }}>
                            {tagSub.label} &rsaquo;
                          </span>
                        )}
                        <span style={{ minWidth:0,fontSize:12,color:"#a89cc8",
                          whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis" }}>{t.title}</span>
                      </div>
                      <span style={{ fontSize:10,color:"#6b6080",flexShrink:0,display:"flex",alignItems:"center",gap:3 }}>
                        <MessageCircle style={{ width:8,height:8 }}/>{t.reply_count??0}
                      </span>
                      <span style={{ fontSize:10,color:"rgba(107,96,128,.45)",flexShrink:0 }}>
                        {rel(t.last_post_at||t.created_at)}
                      </span>
                    </button>
                    );
                  })}
                </>
              );
            })()}
          </div>
        );
      })}
    </div>
  );
}

function InfoLanding({ catId, onSubcat, onBack, onGoHome, onNew }) {
  const { authUser } = useAuth();
  const cat = CATS.find(c=>c.id===catId) || CATS[0];
  const subcats = cat.subcats || [];
  const [recentSub, setRecentSub] = useState({});
  const [loadingSub, setLoadingSub] = useState({});
  const generalSectionRef = useRef(null);
  // For Minecraft: untagged posts that appear after the subcat list
  const [untaggedThreads, setUntaggedThreads] = useState([]);
  const [untaggedLoading, setUntaggedLoading] = useState(false);
  const [untaggedPage, setUntaggedPage] = useState(1);
  const [untaggedTotal, setUntaggedTotal] = useState(0);
  const UNTAG_PER = 20;
  const isMC = catId === "minecraft";
  const isCommunity = catId === "community";

  useEffect(()=>{
    subcats.forEach(sub=>{
      setLoadingSub(p=>({...p,[sub.id]:true}));
      fetch(`${API}/forum/categories/${catId}/threads?page=1&limit=1&tag=${sub.id}`)
        .then(r=>r.json()).then(d=>setRecentSub(p=>({...p,[sub.id]:d.threads||[]})))
        .catch(()=>setRecentSub(p=>({...p,[sub.id]:[]})))
        .finally(()=>setLoadingSub(p=>({...p,[sub.id]:false})));
    });
  },[catId]);

  // Fetch general posts for Minecraft (mc-other tag) and Community (general-discussion tag)
  const loadUntagged = useCallback(()=>{
    if(!isMC && !isCommunity) return;
    setUntaggedLoading(true);
    const tag = isMC ? "mc-other" : "general-discussion";
    fetch(`${API}/forum/categories/${catId}/threads?page=${untaggedPage}&limit=${UNTAG_PER}&tag=${tag}`)
      .then(r=>r.json()).then(d=>{ setUntaggedThreads(d.threads||[]); setUntaggedTotal(d.total||0); })
      .catch(()=>setUntaggedThreads([]))
      .finally(()=>setUntaggedLoading(false));
  },[catId,untaggedPage,isMC,isCommunity]);
  useEffect(()=>{ loadUntagged(); },[loadUntagged]);

  const canPost = !cat.locked && !!authUser;

  return (
    <div className="ff">
      <PageHeader
        crumbs={[{label:"Forum",onClick:onGoHome||onBack}]}
        title={cat.label}
        subtitle={cat.desc}
        color={cat.color}
      />
      {/* Column header */}
      <div style={{ display:"flex",padding:"8px 24px",borderBottom:"1px solid rgba(42,32,64,.6)",
        background:"rgba(255,255,255,.018)" }}>
        <span style={{ flex:1,fontSize:10,fontWeight:600,letterSpacing:1.5,textTransform:"uppercase",
          color:"#6b6080",fontFamily:"'Cinzel',serif" }}>Section</span>
        <span className="fhide" style={{ width:420,fontSize:10,fontWeight:600,letterSpacing:1.5,
          textTransform:"uppercase",color:"#6b6080",fontFamily:"'Cinzel',serif",flexShrink:0,
          textAlign:"center",marginRight:-24 }}>Recent Post</span>
      </div>
      {subcats.map((sub,i)=>{
        const Icon = sub.icon;
        const loading = loadingSub[sub.id];
        const latest = (recentSub[sub.id]||[])[0]||null;
        return (
          <div key={sub.id} className={`fa fa${Math.min(i+1,6)}`}
            style={{ display:"flex",alignItems:"stretch",borderBottom:"1px solid rgba(42,32,64,.4)",
              transition:"background .13s" }}
            onMouseEnter={e=>e.currentTarget.style.background="rgba(123,94,167,.04)"}
            onMouseLeave={e=>e.currentTarget.style.background=""}>
            {/* Left: subcat label — clickable */}
            <button style={{ flex:1,minWidth:0,display:"flex",alignItems:"center",gap:12,
              padding:"14px 24px",background:"none",border:"none",cursor:"pointer",textAlign:"left" }}
              onClick={()=>onSubcat(sub.id)}>
              <div style={{ width:32,height:32,flexShrink:0,borderRadius:7,
                background:`${sub.color}14`,border:`1px solid ${sub.color}24`,
                display:"flex",alignItems:"center",justifyContent:"center" }}>
                {sub.isReligion
                  ? <ReligionIcon size={15} color={sub.color} />
                  : Icon ? <Icon style={{ width:15,height:15,color:sub.color }} /> : null}
              </div>
              <div style={{ minWidth:0 }}>
                <div style={{ fontSize:13,fontWeight:600,color:"#e8e0f0",marginBottom:2 }}>{sub.label}</div>
                <div style={{ fontSize:12,color:"#6b6080" }}>{sub.desc}</div>
              </div>
            </button>
            {/* Right: most recent post as a clickable link in the Recent Post column */}
            <div className="fhide" style={{ width:420,flexShrink:0,display:"flex",alignItems:"center",
              padding:"14px 0",borderLeft:"1px solid rgba(42,32,64,.3)",justifyContent:"center" }}>
              {loading && <Skel w="70%" h={9} mb={0} />}
              {!loading && !latest && (
                <span style={{ fontSize:12,color:"#4a3870",fontStyle:"italic" }}>No posts yet</span>
              )}
              {!loading && latest && (
                <button onClick={e=>{e.stopPropagation();onSubcat(sub.id,threadUrlRef(latest));}}
                  style={{ background:"none",border:"none",cursor:"pointer",padding:"0 20px",
                    textAlign:"left",minWidth:0,width:"100%",display:"flex",alignItems:"center",gap:10 }}>
                  <span style={{ flex:1,minWidth:0,fontSize:12,color:"#a89cc8",fontWeight:500,
                    whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis" }}>
                    {latest.title}
                  </span>
                  <span style={{ display:"flex",alignItems:"center",gap:6,flexShrink:0,fontSize:11,color:"#6b6080" }}>
                    <span style={{ display:"flex",alignItems:"center",gap:3 }}>
                      <MessageCircle style={{ width:9,height:9 }}/>{latest.reply_count??0}
                    </span>
                    <span style={{ color:"rgba(107,96,128,.5)" }}>{rel(latest.last_post_at||latest.created_at)}</span>
                  </span>
                </button>
              )}
            </div>
          </div>
        );
      })}

      {/* ── General posts section (Minecraft: mc-other tag, Community: general-discussion tag) ── */}
      {(isMC || isCommunity) && (()=>{
        const sectionColor = isMC ? "#4ade80" : cat.color;
        const sectionBorder = isMC ? "rgba(74,222,128,.12)" : "rgba(155,127,212,.12)";
        const sectionBg = isMC ? "rgba(74,222,128,.02)" : "rgba(155,127,212,.02)";
        return (
          <>
            <div ref={generalSectionRef} style={{ display:"flex",alignItems:"center",gap:10,
              padding:"10px 24px",borderBottom:"1px solid rgba(42,32,64,.4)",
              borderTop:`2px solid ${sectionBorder}`,
              background:sectionBg }}>
              {isMC
                ? <MCGrassIcon size={14} color={sectionColor} />
                : <MessageCircle style={{ width:14,height:14,color:sectionColor,opacity:.7 }} />}
              <span style={{ fontSize:10,fontFamily:"'Cinzel',serif",letterSpacing:1.5,
                textTransform:"uppercase",color:sectionColor,fontWeight:700,opacity:.7 }}>General Posts</span>
              <span style={{ fontSize:11,color:"#6b6080",fontStyle:"italic",flex:1 }}>
                {isMC ? "— posts not in a specific section" : "— open discussion and general chat"}
              </span>
              {canPost && (
                <button className="fbtn fbtn-p" style={{ padding:"5px 14px",fontSize:11,flexShrink:0 }}
                  onClick={()=>onNew(cat.defaultTag||null)}>
                  <Plus style={{ width:10,height:10 }} /> New Thread
                </button>
              )}
            </div>
            {untaggedLoading && (
              <div style={{ padding:"16px 24px" }}>
                {[1,2,3].map(i=><div key={i} style={{ display:"flex",gap:12,marginBottom:12 }}>
                  <Skel w={28} h={28} mb={0}/><div style={{ flex:1 }}><Skel w="42%" h={11} mb={5}/><Skel w="22%" h={9} mb={0}/></div>
                </div>)}
              </div>
            )}
            {!untaggedLoading && untaggedThreads.length===0 && (
              <div style={{ padding:"28px 24px",textAlign:"center" }}>
                <p style={{ fontSize:12,color:"#4a3870",margin:0,fontStyle:"italic" }}>No general posts yet.</p>
              </div>
            )}
            {!untaggedLoading && untaggedThreads.map(t=>(
              <div key={t.id} className="fth-row" style={{ padding:"11px 24px" }}
                onClick={()=>onSubcat(null,threadUrlRef(t))}>
                <Avatar size={26} role={t.author_role} />
                <div style={{ flex:1,minWidth:0 }}>
                  <div style={{ fontSize:13,fontWeight:600,color:"#e8e0f0",
                    whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",maxWidth:500,marginBottom:2 }}>{t.title}</div>
                  <span style={{ fontSize:11,color:"#6b6080" }}>
                    by <span style={{ color:"#a89cc8" }}>{t.author}</span> &middot; {rel(t.created_at)}
                  </span>
                </div>
                <span style={{ fontSize:12,color:"#a89cc8",flexShrink:0,display:"flex",alignItems:"center",gap:4 }}>
                  <MessageCircle style={{ width:10,height:10 }}/>{t.reply_count??0}
                </span>
              </div>
            ))}
            {Math.ceil(untaggedTotal/UNTAG_PER)>1 && (
              <Pagination page={untaggedPage} pages={Math.ceil(untaggedTotal/UNTAG_PER)} onChange={setUntaggedPage} />
            )}
          </>
        );
      })()}
    </div>
  );
}

function InfoSubView({ catId, subcatId, onThread, onGoCategory, onGoHome, onNew }) {
  const { authUser } = useAuth();
  const parentCat = CATS.find(c=>c.id===catId) || CATS[0];
  const allSubs = [...INFO_SUBCATS, ...COMMUNITY_SUBCATS, ...MC_SUBCATS];
  const sub = allSubs.find(s=>s.id===subcatId);
  const [threads,setThreads]=useState([]); const [loading,setLoading]=useState(true);
  const [page,setPage]=useState(1); const [total,setTotal]=useState(0);
  const PER=20;

  const backLabel = catId==="minecraft" ? "Minecraft" : catId==="community" ? "Community Discussion" : "Information";

  const load=useCallback(()=>{
    setLoading(true);
    fetch(`${API}/forum/categories/${catId}/threads?page=${page}&limit=${PER}&tag=${subcatId}`)
      .then(r=>r.json()).then(d=>{setThreads(d.threads||[]);setTotal(d.total||0);})
      .catch(()=>setThreads([])).finally(()=>setLoading(false));
  },[catId,subcatId,page]);
  useEffect(()=>{load();},[load]);

  return (
    <div className="ff">
      <PageHeader
        crumbs={[{label:"Forum",onClick:onGoHome},{label:backLabel,onClick:()=>onGoCategory(catId)}]}
        title={sub?.label||""}
        subtitle={sub?.desc}
        color={sub?.color||"#9b7fd4"}
      />
      <CreateBar authUser={authUser} onNew={onNew} />
      <ThreadList threads={threads} loading={loading} cat={parentCat} onThread={onThread}
        emptyMsg="No posts in this section yet." authUser={authUser} onNew={onNew} locked={parentCat?.locked} />
      <Pagination page={page} pages={Math.ceil(total/PER)} onChange={setPage} />
    </div>
  );
}

function CatView({ catId, onThread, onGoHome, onNew, onSubcat }) {
  const { authUser } = useAuth();
  const cat = CATS.find(c=>c.id===catId);
  const [threads,setThreads]=useState([]); const [loading,setLoading]=useState(true);
  const [page,setPage]=useState(1); const [total,setTotal]=useState(0);
  const PER=20;

  const load=useCallback(()=>{
    setLoading(true);
    fetch(`${API}/forum/categories/${catId}/threads?page=${page}&limit=${PER}`)
      .then(r=>r.json()).then(d=>{setThreads(d.threads||[]);setTotal(d.total||0);})
      .catch(()=>setThreads([])).finally(()=>setLoading(false));
  },[catId,page]);
  useEffect(()=>{load();},[load]);

  return (
    <div className="ff">
      <PageHeader
        crumbs={[{label:"Forum",onClick:onGoHome}]}
        title={cat?.label||""}
        subtitle={cat?.desc}
        color={cat?.color||"#9b7fd4"}
      />
      <CreateBar authUser={authUser} onNew={onNew} />
      <ThreadList threads={threads} loading={loading} cat={cat} onThread={onThread}
        emptyMsg="No threads yet — be the first to post."
        authUser={authUser} onNew={onNew} locked={cat?.locked} />
      <Pagination page={page} pages={Math.ceil(total/PER)} onChange={setPage} />
    </div>
  );
}

function ThreadList({ threads: initThreads, loading, cat, onThread, emptyMsg, authUser, onNew, locked }) {
  const { accessToken } = useAuth();
  const [threads, setThreads] = useState(initThreads);
  const [busy, setBusy] = useState({});
  useEffect(()=>setThreads(initThreads),[initThreads]);

  const vote = async (e, t, val) => {
    e.stopPropagation();
    if(!authUser || busy[t.id]) return;
    const prev = t.user_vote ?? 0;
    const next = prev===val ? 0 : val;
    setThreads(ts=>ts.map(x=>x.id!==t.id?x:{...x,user_vote:next,vote_count:(Number(x.vote_count)||0)-prev+next}));
    setBusy(b=>({...b,[t.id]:true}));
    try {
      const r = await fetch(`${API}/forum/threads/${t.id}/vote`,{
        method:"POST",headers:{"Content-Type":"application/json","Authorization":`Bearer ${accessToken}`},
        body:JSON.stringify({value:next}),
      });
      const d = await r.json();
      if(r.ok) setThreads(ts=>ts.map(x=>x.id!==t.id?x:{...x,vote_count:d.voteCount,user_vote:d.userVote}));
      else     setThreads(ts=>ts.map(x=>x.id!==t.id?x:{...x,user_vote:prev,vote_count:(Number(x.vote_count)||0)+prev-next}));
    } catch {
      setThreads(ts=>ts.map(x=>x.id!==t.id?x:{...x,user_vote:prev,vote_count:(Number(x.vote_count)||0)+prev-next}));
    } finally { setBusy(b=>({...b,[t.id]:false})); }
  };

  return (
    <div>
      <ColHeader cols={[["Thread"],["Replies","70px","center"],["Views","70px","center"]]} />
      {loading && (
        <div style={{ padding:"20px 24px" }}>
          {[1,2,3,4].map(i=>(
            <div key={i} style={{ display:"flex",gap:12,alignItems:"center",marginBottom:16 }}>
              <Skel w={32} h={32} mb={0} />
              <div style={{ flex:1 }}><Skel w="46%" h={12} mb={6} /><Skel w="24%" h={9} mb={0} /></div>
            </div>
          ))}
        </div>
      )}
      {!loading && threads.length===0 && (
        <div style={{ padding:"60px 24px",textAlign:"center" }}>
          <FileText style={{ width:28,height:28,color:"#4a3870",margin:"0 auto 12px",display:"block" }} />
          <p style={{ fontSize:13,color:"#6b6080",marginBottom:20 }}>{emptyMsg}</p>
          {authUser && !locked &&
            <button className="fbtn fbtn-p" onClick={onNew}><Plus style={{ width:12,height:12 }} /> Start a Thread</button>}
        </div>
      )}
      {!loading && threads.map(t=>(
        <div key={t.id} className="fth-row" style={{ padding:0,gap:0 }} onClick={()=>onThread(threadUrlRef(t))}>
          <VoteWidget
            count={Number(t.vote_count)||0}
            userVote={t.user_vote??0}
            disabled={!authUser||!!busy[t.id]}
            onUp={e=>vote(e,t,1)}
            onDown={e=>vote(e,t,-1)}
          />
          <div style={{ width:10,flexShrink:0,display:"flex",justifyContent:"center" }}>
            {t.pinned===1 && <Pin style={{ width:10,height:10,color:cat?.color||"#c9a84c",opacity:.7 }} />}
          </div>
          <Avatar size={28} role={t.author_role} photoUrl={t.author_photo} />
          <div style={{ flex:1,minWidth:0,padding:"14px 10px 14px 8px" }}>
            <div style={{ display:"flex",alignItems:"center",gap:7,marginBottom:3,flexWrap:"wrap" }}>
              {t.locked===1 && <Lock style={{ width:9,height:9,color:"#6b6080" }} />}
              <span style={{ fontSize:13.5,fontWeight:600,color:"#e8e0f0",
                whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",maxWidth:400 }}>{t.title}</span>
            </div>
            <span style={{ fontSize:11,color:"#6b6080" }}>
              by <span style={{ color:"#a89cc8" }}>{t.author}</span> &middot; {rel(t.created_at)}
            </span>
          </div>
          <div className="fhide" style={{ width:70,textAlign:"center",flexShrink:0 }}>
            <span style={{ fontSize:13,fontWeight:600,color:"#a89cc8" }}>{t.reply_count??0}</span>
          </div>
          <div className="fhide" style={{ width:70,textAlign:"center",flexShrink:0,paddingRight:16 }}>
            <span style={{ fontSize:13,fontWeight:600,color:"#a89cc8" }}>{t.view_count??0}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function Pagination({ page, pages, onChange }) {
  if(pages<=1) return null;
  const nums = [];
  for(let i=1;i<=pages;i++){
    if(i===1||i===pages||Math.abs(i-page)<=2) nums.push(i);
    else if(nums[nums.length-1]!=="...") nums.push("...");
  }
  return (
    <div style={{ display:"flex",justifyContent:"center",alignItems:"center",gap:6,padding:"20px 0" }}>
      <button className="fbtn fbtn-g" style={{ padding:"6px 12px" }} disabled={page===1} onClick={()=>onChange(p=>p-1)}>
        <ChevronLeft style={{ width:12,height:12 }} />
      </button>
      {nums.map((n,i)=> n==="..." ? (
        <span key={`e${i}`} style={{ fontSize:12,color:"#4a3870",padding:"0 4px",lineHeight:1 }}>…</span>
      ) : (
        <button key={n} className="fbtn fbtn-g" onClick={()=>onChange(n)}
          style={{ width:32,height:32,padding:0,
            display:"flex",alignItems:"center",justifyContent:"center",
            background: n===page ? "rgba(99,82,199,.35)" : undefined,
            color: n===page ? "#a89cc8" : "#6b6080",
            borderColor: n===page ? "rgba(99,82,199,.6)" : undefined,
            fontFamily:"'Cinzel',serif", fontSize:12 }}>
          {n}
        </button>
      ))}
      <button className="fbtn fbtn-g" style={{ padding:"6px 12px" }} disabled={page===pages} onClick={()=>onChange(p=>p+1)}>
        <ChevronRight style={{ width:12,height:12 }} />
      </button>
    </div>
  );
}

function PageHeader({ crumbs, title, subtitle, color }) {
  return (
    <div className="fa" style={{ padding:"32px 32px 24px",borderBottom:"1px solid rgba(42,32,64,.6)" }}>
      <div style={{ display:"flex",alignItems:"center",gap:6,marginBottom:14 }}>
        {crumbs.map((c,i)=>(
          <span key={i} style={{ display:"flex",alignItems:"center",gap:6 }}>
            <button className="fcrumb" onClick={c.onClick}>{c.label}</button>
            <ChevronRight style={{ width:11,height:11,color:"#4a3870" }} />
          </span>
        ))}
        <span style={{ fontSize:11,color:"#a89cc8",fontWeight:500 }}>{title}</span>
      </div>
      <div style={{ display:"flex",alignItems:"center",gap:10,marginBottom:6,flexWrap:"wrap" }}>
        <div style={{ width:3,height:22,background:color,borderRadius:2,flexShrink:0 }} />
        <h1 style={{ fontFamily:"'Cinzel',serif",fontSize:"clamp(18px,3vw,26px)",
          fontWeight:700,color:"#e8e0f0",margin:0,letterSpacing:.5 }}>{title}</h1>
      </div>
      {subtitle && <p style={{ fontSize:12.5,color:"#6b6080",margin:"0 0 0 13px",lineHeight:1.5 }}>{subtitle}</p>}
    </div>
  );
}

function ThreadView({ threadId, catId, subcatId, onBack, onGoHome, onGoCategory, showToast }) {
  const { authUser, accessToken } = useAuth();
  const [thread,setThread]=useState(null);
  // catId is only known when the user clicked through from a category page.
  // On a direct /forum/thread/:id visit (a shared link) it's null, so fall
  // back to the category the loaded thread actually belongs to.
  const cat = CATS.find(c=>c.id===(catId || thread?.category_id));
  const resolvedSubcatId = subcatId || thread?.tag || null;
  const sub = [...INFO_SUBCATS,...COMMUNITY_SUBCATS,...MC_SUBCATS].find(s=>s.id===resolvedSubcatId);
  const [posts,setPosts]=useState([]);
  const [loading,setLoading]=useState(true);
  const [body,setBody]=useState("");
  const [submitting,setSubmitting]=useState(false);
  const [pv,setPv]=useState({});
  const [tv,setTv]=useState({count:0,userVote:0,busy:false});
  const [replyPage,setReplyPage]=useState(1);
  const [lockBusy,setLockBusy]=useState(false);
  const REPLIES_PER_PAGE=10;
  const numericThreadId = thread?.id || (/^\d+$/.test(String(threadId)) ? threadId : null);

  const load=useCallback(()=>{
    setLoading(true);
    const h = accessToken ? {"Authorization":`Bearer ${accessToken}`} : {};
    fetch(`${API}/forum/threads/${threadId}`,{headers:h})
      .then(r=>r.json()).then(d=>{
        const t=d.thread||null; const ps=d.posts||[];
        setThread(t); setPosts(ps);
        if(t) setTv({count:Number(t.vote_count)||0,userVote:t.user_vote??0,busy:false});
        const init={};
        ps.forEach(p=>{ init[p.id]={count:Number(p.vote_count)||0,userVote:p.user_vote??0,busy:false}; });
        setPv(init);
      })
      .catch(()=>{}).finally(()=>setLoading(false));
  },[threadId,accessToken]);
  useEffect(()=>{load();},[load]);

  // Numeric /thread/8 links remain valid, but once the thread loads we replace
  // the address bar with its permanent title slug without adding history.
  useEffect(()=>{
    if (!thread?.slug) return;
    const canonical = `${isForumDomain() ? "" : "/forum"}/thread/${thread.slug}`;
    if (window.location.pathname !== canonical)
      window.history.replaceState({ forum:true }, "", canonical);
  },[thread?.slug]);

  const voteThread=async(val)=>{
    if(!authUser||tv.busy) return;
    const next=tv.userVote===val?0:val;
    setTv(v=>({...v,userVote:next,count:v.count-v.userVote+next,busy:true}));
    try{
      const r=await fetch(`${API}/forum/threads/${numericThreadId}/vote`,{
        method:"POST",headers:{"Content-Type":"application/json","Authorization":`Bearer ${accessToken}`},
        body:JSON.stringify({value:next}),
      });
      const d=await r.json();
      if(r.ok) setTv({count:d.voteCount,userVote:d.userVote,busy:false});
      else     setTv(v=>({...v,busy:false}));
    }catch{ setTv(v=>({...v,busy:false})); }
  };

  const votePost=async(postId,val)=>{
    if(!authUser||pv[postId]?.busy) return;
    const prev=pv[postId]||{count:0,userVote:0,busy:false};
    const next=prev.userVote===val?0:val;
    setPv(m=>({...m,[postId]:{...prev,userVote:next,count:prev.count-prev.userVote+next,busy:true}}));
    try{
      const r=await fetch(`${API}/forum/posts/${postId}/vote`,{
        method:"POST",headers:{"Content-Type":"application/json","Authorization":`Bearer ${accessToken}`},
        body:JSON.stringify({value:next}),
      });
      const d=await r.json();
      if(r.ok) setPv(m=>({...m,[postId]:{count:d.voteCount,userVote:d.userVote,busy:false}}));
      else     setPv(m=>({...m,[postId]:{...prev,busy:false}}));
    }catch{ setPv(m=>({...m,[postId]:{...prev,busy:false}})); }
  };

  const deletePost=async(postId)=>{
    if(!window.confirm("Delete this reply?")) return;
    const r=await fetch(`${API}/forum/posts/${postId}`,{
      method:"DELETE",headers:{"Authorization":`Bearer ${accessToken}`},
    });
    if(r.ok){ showToast("Deleted."); load(); }
    else showToast("Failed to delete.");
  };

  const archiveThread=async(reason)=>{
    const r=await fetch(`${API}/forum/threads/${numericThreadId}`,{
      method:"DELETE",headers:{"Content-Type":"application/json","Authorization":`Bearer ${accessToken}`},
      body:JSON.stringify({reason}),
    });
    const d=await r.json();
    if(r.ok && d.archived){ showToast("Thread archived."); onBack(); }
    else if(r.ok){ showToast("Thread deleted."); onBack(); }
    else showToast("Failed.");
  };

  const restoreThread=async()=>{
    const r=await fetch(`${API}/forum/threads/${numericThreadId}/restore`,{
      method:"PATCH",headers:{"Authorization":`Bearer ${accessToken}`},
    });
    if(r.ok){ showToast("Thread restored."); load(); }
    else showToast("Failed to restore.");
  };

  const toggleThreadLock=async()=>{
    if(!isMod(authUser) || lockBusy) return;
    const nextLocked = thread?.locked===1 ? 0 : 1;
    setLockBusy(true);
    try {
      const r=await fetch(`${API}/forum/threads/${numericThreadId}/lock`,{
        method:"PATCH",
        headers:{
          "Content-Type":"application/json",
          "Authorization":`Bearer ${accessToken}`,
        },
        body:JSON.stringify({ locked:nextLocked }),
      });
      let d={};
      try { d=await r.json(); } catch {}
      if(!r.ok) throw new Error(d.error||"Failed to update thread lock.");
      const savedLocked = Number(d.locked ?? nextLocked)===1 ? 1 : 0;
      setThread(t=>t ? {...t,locked:savedLocked} : t);
      showToast(savedLocked ? "Thread locked." : "Thread unlocked.");
    } catch(err) {
      showToast(err.message||"Failed to update thread lock.");
    } finally {
      setLockBusy(false);
    }
  };

  const editThread=async({ body: newBody, title: newTitle })=>{
    const r=await fetch(`${API}/forum/threads/${numericThreadId}/body`,{
      method:"PATCH",
      headers:{"Content-Type":"application/json","Authorization":`Bearer ${accessToken}`},
      body:JSON.stringify({ body:newBody, title:newTitle }),
    });
    const d=await r.json();
    if(r.ok){
      setThread(t=>({...t, body:d.body, title:d.title??t.title, edited_at:d.edited_at }));
      showToast("Post updated.");
    } else {
      showToast(d.error||"Failed to save.");
      throw new Error(d.error);
    }
  };

  const editPost=async(postId,{ body: newBody })=>{
    const r=await fetch(`${API}/forum/posts/${postId}/body`,{
      method:"PATCH",
      headers:{"Content-Type":"application/json","Authorization":`Bearer ${accessToken}`},
      body:JSON.stringify({ body:newBody }),
    });
    const d=await r.json();
    if(r.ok){
      setPosts(ps=>ps.map(p=>p.id!==postId?p:{...p,body:d.body,edited_at:d.edited_at}));
      showToast("Reply updated.");
    } else {
      showToast(d.error||"Failed to save.");
      throw new Error(d.error);
    }
  };

  const setThreadEmbed=async({ embed_html, embed_height })=>{
    const r=await fetch(`${API}/forum/threads/${numericThreadId}/embed`,{
      method:"PATCH",
      headers:{"Content-Type":"application/json","Authorization":`Bearer ${accessToken}`},
      body:JSON.stringify({ embed_html, embed_height }),
    });
    const d=await r.json();
    if(r.ok){
      setThread(t=>({...t, embed_html:d.embed_html, embed_height:d.embed_height }));
      showToast(d.embed_html?"Embed saved.":"Embed removed.");
    } else {
      showToast(d.error||"Failed to save embed.");
      throw new Error(d.error);
    }
  };

  const setPostEmbed=async(postId,{ embed_html, embed_height })=>{
    const r=await fetch(`${API}/forum/posts/${postId}/embed`,{
      method:"PATCH",
      headers:{"Content-Type":"application/json","Authorization":`Bearer ${accessToken}`},
      body:JSON.stringify({ embed_html, embed_height }),
    });
    const d=await r.json();
    if(r.ok){
      setPosts(ps=>ps.map(p=>p.id!==postId?p:{...p,embed_html:d.embed_html,embed_height:d.embed_height}));
      showToast(d.embed_html?"Embed saved.":"Embed removed.");
    } else {
      showToast(d.error||"Failed to save embed.");
      throw new Error(d.error);
    }
  };

  const reply=async()=>{
    if(!body.trim()) return; setSubmitting(true);
    try{
      const r=await fetch(`${API}/forum/threads/${numericThreadId}/posts`,{method:"POST",
        headers:{"Content-Type":"application/json","Authorization":`Bearer ${accessToken}`},
        body:JSON.stringify({body})});
      if(!r.ok) throw new Error();
      setBody(""); load(); showToast("Reply posted.");
    }catch{ showToast("Failed to post reply."); }
    finally{ setSubmitting(false); }
  };

  if(loading) return (
    <div className="ff" style={{ padding:"40px 32px" }}>
      <Skel w={200} h={11} mb={28} />
      <Skel w="58%" h={22} mb={20} />
      {[1,2,3].map(i=>(
        <div key={i} style={{ padding:"20px 0",borderBottom:"1px solid rgba(42,32,64,.5)" }}>
          <Skel w="34%" h={11} mb={10} /><Skel w="70%" h={11} mb={6} /><Skel w="50%" h={11} mb={0} />
        </div>
      ))}
    </div>
  );

  if(!thread) return (
    <div className="ff" style={{ textAlign:"center",padding:"80px 24px" }}>
      <AlertCircle style={{ width:28,height:28,color:"#4a3870",margin:"0 auto 14px",display:"block" }} />
      <p style={{ fontSize:13,color:"#6b6080",marginBottom:24 }}>Thread not found.</p>
      <button className="fbtn fbtn-g" onClick={()=>onBack()}><ChevronLeft style={{ width:12,height:12 }} /> Back</button>
    </div>
  );

  const locked = thread.locked===1;

  const copyThreadLink = () => {
    const url = `${FORUM_ORIGIN}/thread/${threadUrlRef(thread)}`;
    navigator.clipboard.writeText(url)
      .then(()=>showToast?.("Link copied to clipboard"))
      .catch(()=>showToast?.(url));
  };

  const crumbs = [
    { label:"Forum", onClick:onGoHome||onBack },
    {
      label:cat?.label,
      onClick:()=>onGoCategory ? onGoCategory(cat?.id, null) : onBack()
    }
  ];
  if(sub) crumbs.push({
    label:sub.label,
    onClick:()=>onGoCategory ? onGoCategory(cat?.id, resolvedSubcatId) : onBack()
  });

  return (
    <div className="ff">
      <div className="fa" style={{ padding:"32px 32px 20px",borderBottom:"1px solid rgba(42,32,64,.6)" }}>
        <div style={{ display:"flex",alignItems:"center",gap:6,marginBottom:14 }}>
          {crumbs.map((c,i)=>(
            <span key={i} style={{ display:"flex",alignItems:"center",gap:6 }}>
              <button className="fcrumb" onClick={c.onClick}>{c.label}</button>
              <ChevronRight style={{ width:11,height:11,color:"#4a3870" }} />
            </span>
          ))}
          <span style={{ fontSize:11,color:"#6b6080",overflow:"hidden",textOverflow:"ellipsis",
            whiteSpace:"nowrap",maxWidth:200 }}>{thread.title}</span>
        </div>
        <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:10,flexWrap:"wrap" }}>
          {thread.pinned===1 && <span style={{ display:"flex",alignItems:"center",gap:4,
            fontSize:10,fontWeight:700,color:cat?.color,fontFamily:"'Cinzel',serif",letterSpacing:1,
            textTransform:"uppercase" }}><Pin style={{ width:10,height:10 }} />Pinned</span>}
          {locked && <span style={{ display:"flex",alignItems:"center",gap:4,
            fontSize:10,fontWeight:700,color:"#f87171",fontFamily:"'Cinzel',serif",letterSpacing:1,
            textTransform:"uppercase" }}><Lock style={{ width:10,height:10 }} />Locked</span>}
        </div>
        <h1 style={{ fontFamily:"'Cinzel',serif",fontSize:"clamp(16px,2.8vw,22px)",fontWeight:700,
          color:"#e8e0f0",margin:"0 0 12px",lineHeight:1.3 }}>{thread.title}</h1>
        <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:10 }}>
          <div style={{ display:"flex",gap:18,flexWrap:"wrap",fontSize:12,color:"#6b6080" }}>
            <span style={{ display:"flex",alignItems:"center",gap:4 }}>
              <MessageCircle style={{ width:11,height:11 }}/> {posts.length} {posts.length===1?"reply":"replies"}
            </span>
            <span style={{ display:"flex",alignItems:"center",gap:4 }}>
              <Eye style={{ width:11,height:11 }}/> {thread.view_count??0} views
            </span>
            <span style={{ display:"flex",alignItems:"center",gap:4 }}>
              <Clock style={{ width:11,height:11 }}/> {rel(thread.created_at)}
            </span>
          </div>
          <div style={{ display:"flex",gap:8 }}>
            <button className="fbtn fbtn-g" style={{ padding:"5px 14px",fontSize:10 }}
              onClick={copyThreadLink}>
              <Link2 style={{ width:10,height:10 }} /> Copy Link
            </button>
            {authUser && isMod(authUser) && (
              <>
                {!thread.archived && (
                  <button className="fbtn fbtn-g"
                    disabled={lockBusy}
                    style={{ padding:"5px 14px",fontSize:10,
                      color:locked?"#4ade80":"#f87171",
                      borderColor:locked?"rgba(74,222,128,.25)":"rgba(248,113,113,.25)" }}
                    onClick={toggleThreadLock}>
                    {locked
                      ? <><Unlock style={{ width:10,height:10 }} /> {lockBusy?"Updating…":"Unlock Thread"}</>
                      : <><Lock style={{ width:10,height:10 }} /> {lockBusy?"Updating…":"Lock Thread"}</>}
                  </button>
                )}
                {thread.archived ? (
                  <button className="fbtn fbtn-g" style={{ padding:"5px 14px",fontSize:10,color:"#4ade80",borderColor:"rgba(74,222,128,.25)" }}
                    onClick={restoreThread}>
                    <RotateCcw style={{ width:10,height:10 }} /> Restore
                  </button>
                ) : (
                  <button className="fbtn fbtn-g" style={{ padding:"5px 14px",fontSize:10,color:"#f59e0b",borderColor:"rgba(245,158,11,.25)" }}
                    onClick={()=>{ const r=window.prompt("Archive reason (optional):",""); if(r===null) return; archiveThread(r||null); }}>
                    <Archive style={{ width:10,height:10 }} /> Archive
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <PostCard post={{...thread,is_op:true}} cat={cat} num={1}
        vote={tv} canVote={!!authUser&&!tv.busy}
        onVoteUp={()=>voteThread(1)} onVoteDown={()=>voteThread(-1)}
        canDelete={false} onDelete={null}
        canEdit={isMod(authUser)} onEdit={editThread}
        canEmbed={isMod(authUser)} onEditEmbed={setThreadEmbed}
      />

      {(()=>{
        const totalReplyPages = Math.ceil(posts.length/REPLIES_PER_PAGE);
        const pagePosts = posts.slice((replyPage-1)*REPLIES_PER_PAGE, replyPage*REPLIES_PER_PAGE);
        const startNum = (replyPage-1)*REPLIES_PER_PAGE + 2;
        return (
          <>
            {pagePosts.map((p,i)=>{
              const v=pv[p.id]||{count:0,userVote:0,busy:false};
              const canDel=authUser&&(authUser.id===p.author_id||["admin","moderator"].includes(authUser.role));
              return (
                <PostCard key={p.id} post={p} cat={cat} num={startNum+i}
                  vote={v} canVote={!!authUser&&!v.busy}
                  onVoteUp={()=>votePost(p.id,1)} onVoteDown={()=>votePost(p.id,-1)}
                  canDelete={canDel} onDelete={()=>deletePost(p.id)}
                  canEdit={isMod(authUser)} onEdit={(changes)=>editPost(p.id,changes)}
                  canEmbed={isMod(authUser)} onEditEmbed={(changes)=>setPostEmbed(p.id,changes)}
                />
              );
            })}
            {totalReplyPages>1 && (
              <div style={{ display:"flex",justifyContent:"center",alignItems:"center",gap:10,padding:"20px 0",borderBottom:"1px solid rgba(42,32,64,.4)" }}>
                <button className="fbtn fbtn-g" style={{ padding:"6px 14px" }}
                  disabled={replyPage===1}
                  onClick={()=>{ setReplyPage(p=>p-1); window.scrollTo({top:0,behavior:"smooth"}); }}>
                  <ChevronLeft style={{ width:11,height:11 }} /> Prev
                </button>
                <span style={{ fontSize:11,color:"#6b6080",fontFamily:"'Cinzel',serif",letterSpacing:1 }}>
                  Page {replyPage} / {totalReplyPages}
                  <span style={{ color:"#4a3870",marginLeft:8 }}>({posts.length} {posts.length===1?"reply":"replies"})</span>
                </span>
                <button className="fbtn fbtn-g" style={{ padding:"6px 14px" }}
                  disabled={replyPage===totalReplyPages}
                  onClick={()=>{ setReplyPage(p=>p+1); window.scrollTo({top:0,behavior:"smooth"}); }}>
                  Next <ChevronRight style={{ width:11,height:11 }} />
                </button>
              </div>
            )}
          </>
        );
      })()}

      <div className="fdiv" style={{ margin:"0 32px" }} />

      {authUser ? (
        locked ? (
          <div style={{ margin:"0 32px 32px",display:"flex",alignItems:"center",gap:10,
            padding:"14px 18px",borderRadius:8,border:"1px solid rgba(248,113,113,.15)",
            background:"rgba(248,113,113,.04)" }}>
            <Lock style={{ width:13,height:13,color:"#f87171",flexShrink:0 }} />
            <span style={{ fontSize:13,color:"#6b6080" }}>This thread is locked.</span>
          </div>
        ) : (
          <div className="fa" style={{ margin:"0 32px 40px" }}
            onRichsubmit={reply}>
            <div style={{ display:"flex",alignItems:"center",gap:10,marginBottom:12 }}>
              <Avatar size={26} role={authUser.role} photoUrl={authUser.photo_url||authUser.photoUrl||authUser.avatar} />
              <span style={{ fontSize:13,fontWeight:500,color:"#a89cc8" }}>{authUser.username}</span>
              <RolePill role={authUser.role} />
              {isMod(authUser) && (
                <span style={{ fontSize:9,letterSpacing:1,padding:"2px 7px",borderRadius:4,
                  fontFamily:"'Cinzel',serif",fontWeight:700,
                  color:"#c9a84c",background:"rgba(201,168,76,.1)",border:"1px solid rgba(201,168,76,.25)" }}>
                  Rich Editor
                </span>
              )}
            </div>
            {isMod(authUser) ? (
              <RichEditor value={body} onChange={setBody} placeholder="Write your reply…" />
            ) : (
              <textarea className="ftextarea" value={body} onChange={e=>setBody(e.target.value)}
                placeholder="Write your reply…"
                onKeyDown={e=>{ if(e.key==="Enter"&&(e.ctrlKey||e.metaKey)) reply(); }} />
            )}
            <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:10 }}>
              <span style={{ fontSize:11,color:"#4a3870" }}>Ctrl+Enter to submit</span>
              <button className="fbtn fbtn-p" onClick={reply} disabled={submitting||!body.trim()}>
                <Send style={{ width:11,height:11 }} />{submitting?"Posting…":"Post Reply"}
              </button>
            </div>
          </div>
        )
      ) : (
        <div style={{ margin:"0 32px 40px",padding:"20px",textAlign:"center",
          border:"1px solid rgba(42,32,64,.6)",borderRadius:8 }}>
          <p style={{ fontSize:13,color:"#6b6080",margin:0 }}>Log in to reply to this thread.</p>
        </div>
      )}
    </div>
  );
}

// ── Sandboxed embed ──────────────────────────────────────────────────────────
// Renders mod-supplied raw HTML/CSS/JS in an isolated iframe. sandbox=
// "allow-scripts" WITHOUT "allow-same-origin" means the iframe gets its own
// opaque origin: no access to this site's cookies, localStorage, DOM, or
// parent window — a script inside can only affect its own iframe contents.
function SandboxedEmbed({ html, height }) {
  if (!html) return null;
  return (
    <iframe
      title="Embedded content"
      srcDoc={html}
      sandbox="allow-scripts"
      referrerPolicy="no-referrer"
      style={{ width:"100%", height:`${height||600}px`, border:"none", display:"block",
        background:"#0a0810" }}
    />
  );
}

function EmbedEditor({ initialHtml, initialHeight, onSave, onCancel, saving }) {
  const [html, setHtml]     = useState(initialHtml || "");
  const [height, setHeight] = useState(initialHeight || 600);

  return (
    <div style={{ padding:"16px 20px", borderTop:"1px dashed rgba(201,168,76,.3)" }}>
      <label style={{ display:"block",fontSize:10,fontWeight:600,color:"#6b6080",
        textTransform:"uppercase",letterSpacing:1,marginBottom:6,fontFamily:"'Cinzel',serif" }}>
        Raw HTML / CSS / JS embed (mod only — renders in a sandboxed iframe)
      </label>
      <textarea
        value={html}
        onChange={e=>setHtml(e.target.value)}
        placeholder="Paste full HTML document or a snippet…"
        spellCheck={false}
        style={{ width:"100%",minHeight:180,resize:"vertical",fontFamily:"monospace",fontSize:12,
          padding:10,background:"rgba(255,255,255,.03)",border:"1px solid #2a2040",borderRadius:6,
          color:"#c8c0e0",outline:"none" }}
      />
      <div style={{ display:"flex",alignItems:"center",gap:10,marginTop:10 }}>
        <label style={{ fontSize:11,color:"#6b6080" }}>Height (px)</label>
        <input type="number" min={100} max={4000} value={height}
          onChange={e=>setHeight(e.target.value)}
          className="finput" style={{ width:100 }} />
      </div>
      <div style={{ display:"flex",gap:8,justifyContent:"flex-end",marginTop:12 }}>
        {initialHtml && (
          <button className="fbtn fbtn-g" style={{ padding:"6px 14px" }}
            disabled={saving}
            onClick={()=>onSave({ embed_html:null, embed_height:null })}>
            Remove Embed
          </button>
        )}
        <button className="fbtn fbtn-g" style={{ padding:"6px 14px" }}
          onClick={onCancel} disabled={saving}>
          <X style={{ width:10,height:10 }} /> Cancel
        </button>
        <button className="fbtn fbtn-p" style={{ padding:"6px 14px" }}
          onClick={()=>onSave({ embed_html:html, embed_height:height })}
          disabled={saving||!html.trim()}>
          <Check style={{ width:10,height:10 }} />{saving?"Saving…":"Save Embed"}
        </button>
      </div>
    </div>
  );
}

function PostCard({ post, cat, num, vote, canVote, onVoteUp, onVoteDown, canDelete, onDelete, canEdit, onEdit, canEmbed, onEditEmbed }) {
  const op = post.is_op;
  const [editing, setEditing] = useState(false);
  const [editBody, setEditBody] = useState("");
  const [editTitle, setEditTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [embedding, setEmbedding] = useState(false);
  const [embedSaving, setEmbedSaving] = useState(false);

  const startEdit = () => {
    setEditBody(post.body || "");
    setEditTitle(post.title || "");
    setEditing(true);
  };
  const cancelEdit = () => setEditing(false);

  const saveEdit = async () => {
    setSaving(true);
    try {
      await onEdit({ body: editBody, title: op ? editTitle : undefined });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const editBodyIsEmpty = !editBody.trim() ||
    (isHtmlBody(editBody) && !getHtmlContent(editBody)?.replace(/<[^>]*>/g,"").trim());

  const saveEmbed = async (changes) => {
    setEmbedSaving(true);
    try {
      await onEditEmbed(changes);
      setEmbedding(false);
    } finally {
      setEmbedSaving(false);
    }
  };

  return (
    <div style={{ display:"flex",borderBottom:"1px solid rgba(42,32,64,.5)" }}>
      {op && <div style={{ width:2,background:cat?.color||"#9b7fd4",flexShrink:0,opacity:.6 }} />}
      <VoteWidget
        count={vote?.count||0}
        userVote={vote?.userVote||0}
        disabled={!canVote}
        onUp={onVoteUp}
        onDown={onVoteDown}
      />
      <div className="fauth">
        <Avatar size={40} role={post.author_role} photoUrl={post.author_photo} />
        <div style={{ fontSize:12,fontWeight:600,color:"#a89cc8",letterSpacing:.2,marginTop:2 }}>{post.author}</div>
        <RolePill role={post.author_role} />
        {op && <span style={{ fontSize:9,letterSpacing:1,padding:"2px 7px",borderRadius:4,
          fontFamily:"'Cinzel',serif",fontWeight:700,marginTop:2,
          color:cat?.color||"#9b7fd4",background:`${cat?.color||"#9b7fd4"}12`,
          border:`1px solid ${cat?.color||"#9b7fd4"}25` }}>OP</span>}
      </div>
      <div style={{ flex:1,minWidth:0 }}>
        {/* ── Mobile author row (hidden on desktop where .fauth sidebar shows) ── */}
        <div className="fauth-mobile">
          <Avatar size={28} role={post.author_role} photoUrl={post.author_photo} />
          <span style={{ fontSize:12,fontWeight:600,color:"#a89cc8" }}>{post.author}</span>
          <RolePill role={post.author_role} />
          {op && <span style={{ fontSize:9,letterSpacing:1,padding:"2px 7px",borderRadius:4,
            fontFamily:"'Cinzel',serif",fontWeight:700,
            color:"#9b7fd4",background:"rgba(155,127,212,.12)",
            border:"1px solid rgba(155,127,212,.25)" }}>OP</span>}
        </div>
        {/* ── Post header bar ── */}
        <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",
          padding:"10px 20px",borderBottom:"1px solid rgba(42,32,64,.4)",
          background:op?"rgba(123,94,167,.03)":"transparent" }}>
          <span style={{ fontSize:11,color:"#6b6080",display:"flex",alignItems:"center",gap:5 }}>
            <Clock style={{ width:10,height:10 }}/> {rel(post.created_at)}
            {post.edited_at && (
              <span style={{ color:"#4a3870",fontStyle:"italic" }}>
                · edited {rel(post.edited_at)}
              </span>
            )}
          </span>
          <div style={{ display:"flex",alignItems:"center",gap:8 }}>
            <span style={{ fontSize:10,color:"#4a3870",fontFamily:"'Cinzel',serif",letterSpacing:1 }}>#{num}</span>
            {canEdit && onEdit && !editing && (
              <button onClick={e=>{e.stopPropagation();startEdit();}}
                title="Edit"
                style={{ background:"none",border:"none",cursor:"pointer",color:"rgba(155,127,212,.35)",
                  display:"flex",alignItems:"center",padding:2,transition:"color .15s" }}
                onMouseEnter={e=>e.currentTarget.style.color="#9b7fd4"}
                onMouseLeave={e=>e.currentTarget.style.color="rgba(155,127,212,.35)"}>
                <Pencil style={{ width:11,height:11 }} />
              </button>
            )}
            {canEmbed && onEditEmbed && !editing && (
              <button onClick={e=>{e.stopPropagation();setEmbedding(v=>!v);}}
                title={post.embed_html ? "Edit embed" : "Add embed"}
                style={{ background:"none",border:"none",cursor:"pointer",
                  color: embedding||post.embed_html ? "#c9a84c" : "rgba(201,168,76,.35)",
                  display:"flex",alignItems:"center",padding:2,transition:"color .15s" }}
                onMouseEnter={e=>e.currentTarget.style.color="#c9a84c"}
                onMouseLeave={e=>e.currentTarget.style.color=embedding||post.embed_html?"#c9a84c":"rgba(201,168,76,.35)"}>
                <Code2 style={{ width:11,height:11 }} />
              </button>
            )}
            {canDelete && onDelete && !editing && (
              <button onClick={e=>{e.stopPropagation();onDelete();}}
                title="Delete"
                style={{ background:"none",border:"none",cursor:"pointer",color:"rgba(248,113,113,.35)",
                  display:"flex",alignItems:"center",padding:2,transition:"color .15s" }}
                onMouseEnter={e=>e.currentTarget.style.color="#f87171"}
                onMouseLeave={e=>e.currentTarget.style.color="rgba(248,113,113,.35)"}>
                <Trash2 style={{ width:11,height:11 }} />
              </button>
            )}
          </div>
        </div>

        {/* ── Body / edit mode ── */}
        {editing ? (
          <div style={{ padding:"16px 20px" }}>
            {op && (
              <div style={{ marginBottom:12 }}>
                <label style={{ display:"block",fontSize:10,fontWeight:600,color:"#6b6080",
                  textTransform:"uppercase",letterSpacing:1,marginBottom:6,fontFamily:"'Cinzel',serif" }}>Title</label>
                <input className="finput" value={editTitle} onChange={e=>setEditTitle(e.target.value)}
                  maxLength={150} placeholder="Thread title…" />
              </div>
            )}
            <div style={{ marginBottom:10 }}>
              <label style={{ display:"block",fontSize:10,fontWeight:600,color:"#6b6080",
                textTransform:"uppercase",letterSpacing:1,marginBottom:6,fontFamily:"'Cinzel',serif" }}>Content</label>
              <RichEditor value={editBody} onChange={setEditBody} placeholder="Edit your post…" />
            </div>
            <div style={{ display:"flex",gap:8,justifyContent:"flex-end" }}>
              <button className="fbtn fbtn-g" style={{ padding:"6px 14px" }}
                onClick={cancelEdit} disabled={saving}>
                <X style={{ width:10,height:10 }} /> Cancel
              </button>
              <button className="fbtn fbtn-p" style={{ padding:"6px 14px" }}
                onClick={saveEdit} disabled={saving||editBodyIsEmpty||(op&&!editTitle.trim())}>
                <Check style={{ width:10,height:10 }} />{saving?"Saving…":"Save"}
              </button>
            </div>
          </div>
        ) : (
          <>
            {isHtmlBody(post.body) ? (
              <div className="fpost-html" style={{ padding:"16px 20px",fontSize:14,color:"#c8c0e0",lineHeight:1.78,wordBreak:"break-word" }}
                dangerouslySetInnerHTML={{ __html: renderRichPost(getHtmlContent(post.body)) }} />
            ) : (
              <div className="fpost-html" style={{ padding:"16px 20px",fontSize:14,color:"#c8c0e0",lineHeight:1.78,wordBreak:"break-word" }}
                dangerouslySetInnerHTML={{ __html: renderPlainPost(post.body || "") }} />
            )}
            <SandboxedEmbed html={post.embed_html} height={post.embed_height} />
            {embedding && (
              <EmbedEditor
                initialHtml={post.embed_html}
                initialHeight={post.embed_height}
                saving={embedSaving}
                onSave={saveEmbed}
                onCancel={()=>setEmbedding(false)}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ArchivesView({ onThread, onGoHome }) {
  const { authUser, accessToken } = useAuth();
  const [threads,setThreads]=useState([]); const [loading,setLoading]=useState(true);
  const [page,setPage]=useState(1); const [total,setTotal]=useState(0);
  const PER=20;

  const load=useCallback(()=>{
    setLoading(true);
    fetch(`${API}/forum/archives?page=${page}&limit=${PER}`,{
      headers:{"Authorization":`Bearer ${accessToken}`}
    }).then(r=>r.json()).then(d=>{setThreads(d.threads||[]);setTotal(d.total||0);})
    .catch(()=>setThreads([])).finally(()=>setLoading(false));
  },[page,accessToken]);
  useEffect(()=>{load();},[load]);

  const CATS_MAP = Object.fromEntries(CATS.map(c=>[c.id,c]));

  return (
    <div className="ff">
      <PageHeader
        crumbs={[{label:"Forum",onClick:onGoHome}]}
        title="Archives"
        subtitle="Threads removed from public view by moderators."
        color="#6b6080"
      />
      <ColHeader cols={[["Thread"],["Archived By","120px","left"],["Reason","160px","left"]]} />
      {loading && <div style={{ padding:"20px 24px" }}>{[1,2,3].map(i=><div key={i} style={{ marginBottom:12 }}><Skel w="55%" h={11} mb={6}/><Skel w="30%" h={9} mb={0}/></div>)}</div>}
      {!loading && threads.length===0 && (
        <div style={{ padding:"60px 24px",textAlign:"center" }}>
          <Archive style={{ width:28,height:28,color:"#4a3870",margin:"0 auto 12px",display:"block" }} />
          <p style={{ fontSize:13,color:"#6b6080" }}>No archived threads.</p>
        </div>
      )}
      {!loading && threads.map(t=>{
        const cat = CATS_MAP[t.category_id];
        return (
          <div key={t.id} className="fth-row" style={{ padding:0,gap:0 }} onClick={()=>onThread(threadUrlRef(t),t.category_id)}>
            <div style={{ flex:1,minWidth:0,padding:"13px 20px" }}>
              <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:3,flexWrap:"wrap" }}>
                {cat && <span style={{ fontSize:9,padding:"1px 6px",borderRadius:3,
                  background:`${cat.color}14`,border:`1px solid ${cat.color}22`,
                  color:cat.color,fontFamily:"'Cinzel',serif",letterSpacing:1 }}>{cat.label}</span>}
                <span style={{ fontSize:13,fontWeight:600,color:"#a89cc8",
                  whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",maxWidth:380 }}>{t.title}</span>
              </div>
              <span style={{ fontSize:11,color:"#6b6080" }}>
                by <span style={{ color:"#a89cc8" }}>{t.author}</span> &middot; {rel(t.created_at)}
              </span>
            </div>
            <div className="fhide" style={{ width:120,padding:"13px 14px",flexShrink:0 }}>
              <div style={{ fontSize:11,color:"#f59e0b" }}>{t.archived_by||"—"}</div>
              <div style={{ fontSize:10,color:"#6b6080",marginTop:2 }}>{t.archived_at?rel(t.archived_at):""}</div>
            </div>
            <div className="fhide" style={{ width:160,padding:"13px 14px 13px 0",flexShrink:0 }}>
              <span style={{ fontSize:11,color:"#6b6080",fontStyle:"italic" }}>{t.archive_reason||"—"}</span>
            </div>
          </div>
        );
      })}
      <Pagination page={page} pages={Math.ceil(total/PER)} onChange={setPage} />
    </div>
  );
}

function NewModal({ catId, subcatId, onClose, onSuccess, showToast }) {
  const { accessToken, authUser } = useAuth();
  const cat = CATS.find(c=>c.id===catId);
  const sub = [...INFO_SUBCATS,...COMMUNITY_SUBCATS,...MC_SUBCATS].find(s=>s.id===subcatId);
  const [title,setTitle]=useState(""); const [body,setBody]=useState(""); const [busy,setBusy]=useState(false);
  const isRich = !!authUser;

  // isBodyEmpty: works for both plain text and HTML-prefixed body
  const bodyIsEmpty = !body.trim() || (isHtmlBody(body) && !getHtmlContent(body)?.replace(/<[^>]*>/g,"").trim());

  const go=async()=>{
    if(!title.trim()||bodyIsEmpty){ showToast("Please fill in all fields."); return; }
    setBusy(true);
    try{
      const r=await fetch(`${API}/forum/categories/${catId}/threads`,{method:"POST",
        headers:{"Content-Type":"application/json","Authorization":`Bearer ${accessToken}`},
        body:JSON.stringify({title,body,tag:subcatId||cat?.defaultTag||undefined})});
      const d=await r.json();
      if(!r.ok) throw new Error(d.error||"Failed");
      showToast("Thread created."); onSuccess(d.slug || d.threadId);
    }catch(e){ showToast(e.message||"Failed."); }
    finally{ setBusy(false); }
  };

  return (
    <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,.7)",backdropFilter:"blur(4px)",
      zIndex:600,display:"flex",alignItems:"center",justifyContent:"center",padding:20 }}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{ background:"#0f0f1a",border:"1px solid #2a2040",borderRadius:12,
        width:"100%",maxWidth:isRich?700:560,boxShadow:"0 24px 80px rgba(0,0,0,.7)",overflow:"hidden",
        maxHeight:"90vh",display:"flex",flexDirection:"column" }}>
        <div style={{ height:2,background:`linear-gradient(90deg,transparent,${cat?.color||"#9b7fd4"},transparent)`,flexShrink:0 }} />
        <div style={{ padding:"18px 22px",borderBottom:"1px solid #2a2040",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0 }}>
          <div style={{ display:"flex",alignItems:"center",gap:10 }}>
            <div>
              <div style={{ fontFamily:"'Cinzel',serif",fontSize:14,fontWeight:700,color:"#e8e0f0",marginBottom:2 }}>New Thread</div>
              <div style={{ fontSize:12,color:"#6b6080" }}>in {sub?sub.label:cat?.label}</div>
            </div>
            {isRich && (
              <span style={{ fontSize:9,letterSpacing:1,padding:"2px 7px",borderRadius:4,
                fontFamily:"'Cinzel',serif",fontWeight:700,marginLeft:4,
                color:"#c9a84c",background:"rgba(201,168,76,.1)",border:"1px solid rgba(201,168,76,.25)" }}>
                Rich Editor
              </span>
            )}
          </div>
          <button onClick={onClose} style={{ background:"none",border:"1px solid #2a2040",borderRadius:6,
            cursor:"pointer",color:"#6b6080",padding:"4px 8px",fontSize:18,lineHeight:1 }}>×</button>
        </div>
        <div style={{ padding:"20px 22px",overflowY:"auto",flex:1 }}>
          <div style={{ marginBottom:14 }}>
            <label style={{ display:"block",fontSize:11,fontWeight:600,color:"#6b6080",
              textTransform:"uppercase",letterSpacing:1,marginBottom:7,fontFamily:"'Cinzel',serif" }}>Title</label>
            <input className="finput" value={title} onChange={e=>setTitle(e.target.value)}
              placeholder="Thread title…" maxLength={150} />
            <div style={{ textAlign:"right",fontSize:11,color:"#4a3870",marginTop:4 }}>{title.length}/150</div>
          </div>
          <div style={{ marginBottom:18 }}>
            <label style={{ display:"block",fontSize:11,fontWeight:600,color:"#6b6080",
              textTransform:"uppercase",letterSpacing:1,marginBottom:7,fontFamily:"'Cinzel',serif" }}>Content</label>
            {isRich ? (
              <RichEditor value={body} onChange={setBody} placeholder="Write your post…" />
            ) : (
              <textarea className="ftextarea" value={body} onChange={e=>setBody(e.target.value)}
                placeholder="Write your post…" rows={7} />
            )}
          </div>
          <div style={{ display:"flex",gap:10,justifyContent:"flex-end" }}>
            <button className="fbtn fbtn-g" onClick={onClose}>Cancel</button>
            <button className="fbtn fbtn-p" onClick={go} disabled={busy||!title.trim()||bodyIsEmpty}>
              <Send style={{ width:11,height:11 }} />{busy?"Posting…":"Post Thread"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function slugifyThreadTitle(value = '') {
  return String(value)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 160) || 'thread';
}

function threadUrlRef(thread) {
  // Only use a real persisted slug returned by the API. Falling back to a
  // client-generated title slug can create a URL the backend does not know.
  return thread?.slug || String(thread?.id || '');
}

// ── URL-based forum navigation ────────────────────────────────────────────────
// The URL is the single source of truth. There is no saved forum navigation
// stack and no sessionStorage state to restore.
const SUBCAT_TO_SLUG = {
  announcements: "announcements",
  rules: "rules",
  "general-guides": "resources",
  "general-discussion": "general-discussion",
  fitness: "fitness",
  religion: "religion",
  "mc-updates": "updates",
  "mc-guides": "guides",
  suggestions: "suggestions",
  factions: "factions",
};
const SLUG_TO_SUBCAT = Object.fromEntries(
  Object.entries(SUBCAT_TO_SLUG).map(([id, slug]) => [slug, id])
);

function forumHomeEntry() {
  return { view:"home", activeCat:null, activeSubcat:null, activeThread:null };
}

function entryFromProps(initialCat, initialSubcat, initialThread) {
  if (initialThread) return { view:"thread", activeCat:null, activeSubcat:null, activeThread:initialThread };
  if (initialCat && initialSubcat) return { view:"info-subcat", activeCat:initialCat, activeSubcat:initialSubcat, activeThread:null };
  if (initialCat) return { view:"info-landing", activeCat:initialCat, activeSubcat:null, activeThread:null };
  return forumHomeEntry();
}

function entryFromPath(pathname = window.location.pathname) {
  const raw = pathname.replace(/\/+$/, "") || "/";
  // On forums.theonesabove.com the public URLs intentionally omit /forum.
  // Normalise them here so the existing forum route logic stays unchanged.
  const clean = isForumDomain()
    ? (raw === "/" ? "/forum" : `/forum${raw}`)
    : raw;
  if (clean === "/forum") return forumHomeEntry();

  let m = clean.match(/^\/forum\/thread\/([^/]+)$/);
  if (m) return { view:"thread", activeCat:null, activeSubcat:null, activeThread:decodeURIComponent(m[1]) };

  if (clean === "/forum/archives")
    return { view:"archives", activeCat:null, activeSubcat:null, activeThread:null };

  m = clean.match(/^\/forum\/([^/]+)(?:\/([^/]+))?$/);
  if (m) {
    const catId = m[1];
    const cat = CATS.find(c=>c.id===catId);
    if (!cat) return null;
    if (!m[2]) return { view:cat.hasSubcats?"info-landing":"category", activeCat:catId, activeSubcat:null, activeThread:null };

    const subcatId = SLUG_TO_SUBCAT[m[2]] || m[2];
    const validSub = (cat.subcats||[]).some(sub=>sub.id===subcatId);
    if (validSub) return { view:"info-subcat", activeCat:catId, activeSubcat:subcatId, activeThread:null };
  }

  return null;
}

function urlFor(entry) {
  const prefix = isForumDomain() ? "" : "/forum";
  if (!entry) return prefix || "/";
  if (entry.view === "thread" && entry.activeThread) return `${prefix}/thread/${entry.activeThread}`;
  if (entry.view === "archives") return `${prefix}/archives`;
  if (entry.activeCat && entry.activeSubcat) {
    const slug = SUBCAT_TO_SLUG[entry.activeSubcat] || entry.activeSubcat;
    return `${prefix}/${entry.activeCat}/${slug}`;
  }
  if (entry.activeCat) return `${prefix}/${entry.activeCat}`;
  return prefix || "/";
}

export default function ForumPage({ showToast, initialCat, initialSubcat, initialThread }) {
  const { authUser } = useAuth();
  const [showNew,setShowNew]=useState(false);
  const [pendingTag,setPendingTag]=useState(null);
  const [stats,setStats]=useState(null);
  const [statsL,setStatsL]=useState(true);

  const [route, setRoute] = useState(()=>
    entryFromPath(window.location.pathname) || entryFromProps(initialCat, initialSubcat, initialThread)
  );
  const { view, activeCat, activeSubcat, activeThread } = route;

  const navigate = useCallback((entry, { replace=false } = {})=>{
    const url = urlFor(entry);
    if (replace) window.history.replaceState({ forum:true }, "", url);
    else window.history.pushState({ forum:true }, "", url);
    setRoute(entry);
    window.scrollTo({top:0,behavior:"smooth"});
  },[]);

  // Browser Back / Forward simply reads the URL again.
  useEffect(()=>{
    const onPop = ()=>{
      const next = entryFromPath(window.location.pathname);
      if (next) setRoute(next);
      window.scrollTo({top:0,behavior:"smooth"});
    };
    window.addEventListener("popstate", onPop);
    return ()=>window.removeEventListener("popstate", onPop);
  },[]);

  // Honour route changes initiated outside ForumPage, such as the main navbar.
  useEffect(()=>{
    const fromUrl = entryFromPath(window.location.pathname);
    const next = fromUrl || entryFromProps(initialCat, initialSubcat, initialThread);
    setRoute(next);
  },[initialCat, initialSubcat, initialThread]);

  const goHome = useCallback(()=>navigate(forumHomeEntry()),[navigate]);
  const back = useCallback(()=>window.history.back(),[]);

  useEffect(()=>{ document.title="TOA Forums"; return ()=>{ document.title="The Ones Above"; }; },[]);

  useEffect(()=>{
    fetch(`${API}/forum/stats`).then(r=>r.json()).then(d=>setStats(d)).catch(()=>setStats(null)).finally(()=>setStatsL(false));
  },[]);

  const selCat=(id,threadId=null)=>{
    const cat = CATS.find(c=>c.id===id);
    if(threadId){
      navigate({ view:"thread", activeCat:id, activeSubcat:null, activeThread:threadId });
    } else if(cat?.hasSubcats){
      navigate({ view:"info-landing", activeCat:id, activeSubcat:null, activeThread:null });
    } else {
      navigate({ view:"category", activeCat:id, activeSubcat:null, activeThread:null });
    }
  };
  const selSubcat=(catId,subcatId)=>{
    navigate({ view:"info-subcat", activeCat:catId, activeSubcat:subcatId, activeThread:null });
  };
  const selThread=(id)=>{
    navigate({ view:"thread", activeCat, activeSubcat, activeThread:id });
  };
  const goCategory=(catId,subcatId=null)=>{
    const cat = CATS.find(c=>c.id===catId);
    if(subcatId){
      navigate({ view:"info-subcat", activeCat:catId, activeSubcat:subcatId, activeThread:null });
    } else if(cat?.hasSubcats){
      navigate({ view:"info-landing", activeCat:catId, activeSubcat:null, activeThread:null });
    } else {
      navigate({ view:"category", activeCat:catId, activeSubcat:null, activeThread:null });
    }
  };
  const goArchives=()=>navigate({ view:"archives", activeCat:null, activeSubcat:null, activeThread:null });

  return (
    <div style={{ minHeight:"calc(100vh - 62px)", position:"relative" }}>
      <div style={{ position:"fixed",inset:0,pointerEvents:"none",zIndex:0,
        background:`radial-gradient(ellipse 90% 55% at 50% 0%, rgba(123,94,167,.18) 0%, transparent 60%),
          radial-gradient(ellipse 50% 40% at 15% 80%, rgba(201,168,76,.07) 0%, transparent 55%),
          radial-gradient(ellipse 40% 35% at 85% 60%, rgba(123,94,167,.08) 0%, transparent 50%)` }} />
      <ForumPageCanvas />
      <div style={{ position:"relative",zIndex:2,maxWidth:1600,margin:"0 auto",padding:"0 clamp(0px,3vw,48px)" }}>
      <style dangerouslySetInnerHTML={{ __html:CSS }} />

      {view==="home" && (
        <Home onSelect={selCat}
          onSubcat={(catId,subcatId)=>selSubcat(catId,subcatId)}
          stats={stats} statsLoading={statsL} onArchives={goArchives} authUser={authUser} />
      )}
      {view==="info-landing" && <InfoLanding catId={activeCat}
        onSubcat={(subcatId,threadId)=>threadId
          ? navigate({ view:"thread", activeCat, activeSubcat:subcatId, activeThread:threadId })
          : selSubcat(activeCat,subcatId)}
        onBack={back} onGoHome={goHome}
        onNew={(tag)=>{ setPendingTag(tag||null); setShowNew(true); }} />}
      {view==="info-subcat" && activeSubcat && (
        <InfoSubView catId={activeCat} subcatId={activeSubcat} onThread={selThread} onGoCategory={goCategory} onGoHome={goHome} onNew={()=>setShowNew(true)} />
      )}
      {view==="category" && activeCat && (
        <CatView catId={activeCat} onThread={selThread} onGoHome={goHome} onNew={()=>{ setPendingTag(null); setShowNew(true); }} />
      )}
      {view==="thread" && activeThread && (
        <ThreadView threadId={activeThread} catId={activeCat} subcatId={activeSubcat} onBack={back} onGoHome={goHome} onGoCategory={goCategory} showToast={showToast} />
      )}
      {view==="archives" && authUser && isMod(authUser) && (
        <ArchivesView
          onThread={(id,catId)=>navigate({ view:"thread", activeCat:catId, activeSubcat:null, activeThread:id })}
          onGoHome={goHome} />
      )}
      {showNew && activeCat && (
        <NewModal catId={activeCat} subcatId={pendingTag||activeSubcat}
          onClose={()=>{ setShowNew(false); setPendingTag(null); }}
          onSuccess={id=>{ setShowNew(false); selThread(id); }}
          showToast={showToast} />
      )}
      </div>
    </div>
  );
}